import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver"; // Import file-saver for downloading files

//Import Helpers functions
import {
  convertExcelTime,
  convertExcelDate,
  getFormattedCurrentDateYYYYMMDD,
} from "./handlers/helpers";

// Import ALL custom API scripts that can be used for fetching or creating entities
import createQueue from "./api_scripts/create/createQueue";
import getqueueList from "./api_scripts/get/getQueues";
import getAddressBook from "./api_scripts/get/getAddressBook";
import createAddressBook from "./api_scripts/create/createAddressBook";
import createAddressBookEntry from "./api_scripts/create/createAddressBookEntry";
import getaddressbookEntries from "./api_scripts/get/getAddressBookEntries";
import getWorkTypes from "./api_scripts/get/getWorkTypes";
import createAuxCode from "./api_scripts/create/createAuxCode";
import createHolidayList from "./api_scripts/create/createHolidayList";
import getholidayList from "./api_scripts/get/getHolidayList";
import getoverrides from "./api_scripts/get/getOverrideList";
import createBusinessHours from "./api_scripts/create/createBusinessHours";
import getBusinessHours from "./api_scripts/get/getBusinessHours"; // NEW: Import getBusinessHours
import createOverrideList from "./api_scripts/create/createOverrideList";
import createDesktopProfile from "./api_scripts/create/createDesktopProfile";
import getDesktopProfiles from "./api_scripts/get/getDesktopProfile"; // Already imported
import createEntryPoint from "./api_scripts/create/createEntryPoint";
import getEntryPoint from "./api_scripts/get/getEntryPoints"; // NEW: Import getEntryPoints
import createSite from "./api_scripts/create/createSite";
import getSite from "./api_scripts/get/getSite"; // Now returns array of sites
import getmultimediaList from "./api_scripts/get/getMultimediaList"; // Now returns array of profiles
import createSkill from "./api_scripts/create/createSkill";
import getSkillDefinitions from "./api_scripts/get/getSkillDefinitions"; // Now returns array of definitions
import createSkillProfile from "./api_scripts/create/createSkillProfile";
import getSkillProfile from "./api_scripts/get/getSkillProfile"; // Now returns array of profiles
import createTeam from "./api_scripts/create/createTeam";
import getTeam from "./api_scripts/get/getTeam"; // Now returns array of teams
import getAuxCodes from "./api_scripts/get/getAuxCodes"; // Corrected import for getAuxCodes
import getAudioFiles from "./api_scripts/get/getAudioFiles";
import createCADVariable from "./api_scripts/create/createCADVar";
import getCADvariable from "./api_scripts/get/getCADVars";
import createUserProfile from "./api_scripts/create/createUserProfile";
import getuserProfiles from "./api_scripts/get/getUserProfile";


// Import the new modular cache utilities
import { getOrFetchOrCreateAndCache, addToCache, getFromCache } from "./utils/cacheUtils";


const SpreadsheetProcessor = ({ soapCredentials }) => {
  // Progress messages will now be objects { type: 'info'|'success'|'warning'|'error', message: '...', timestamp: '...' }
  const [progress, setProgress] = useState([]);
  const [fileData, setFileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const orgid = soapCredentials.org_id || null;
  const accesstoken = soapCredentials.access_token || null;
  const timezone = soapCredentials.timezone || "America/New_York";

  // Consolidating all caches into cacheRegistry for consistent access
  const cacheRegistry = useRef({
    SkillDefinitionCache: new Map(),
    SkillProfileCache: new Map(), // Added SkillProfileCache
    TeamIdCache: new Map(),
    SiteCache: new Map(),
    AddressBookCache: new Map(),
    // AddressBookEntryCache will be dynamically named, e.g., AddressBookEntries_ID
    HolidayListCache: new Map(),
    OverridesCache: new Map(),
    BusinessHoursCache: new Map(),
    QueueIdCache: new Map(),
    MultimediaProfileCache: new Map(),
    AuxCodeCache: new Map(),
    DesktopProfileCache: new Map(),
    EntryPointCache: new Map(),
    CADVariableCache: new Map(),
    UserProfileCache: new Map()
  });

  // Helper function to add progress messages
  const addProgressMessage = (message, type = "info") => {
    setProgress((prev) => [
      ...prev,
      { type, message, timestamp: new Date().toLocaleTimeString() },
    ]);
  };

  // Function to handle file upload and reading
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      setFileData(workbook);
      addProgressMessage(
        "File uploaded successfully. Processing workbook...",
        "info"
      );
      console.log("Workbook data:", workbook);
    };

    reader.readAsArrayBuffer(file);
  };

  // Function to export the progress log
  const handleExportLog = () => {
    const logContent = progress
      .map(
        (item) =>
          `[${item.timestamp}] [${item.type.toUpperCase()}] ${item.message}`
      )
      .join("\n");
    const blob = new Blob([logContent], { type: "text/plain;charset=utf-8" });
    saveAs(
      blob,
      `SpreadsheetProcessor_Log_${new Date().toISOString().slice(0, 10)}.txt`
    );
  };

  // Function to run the script based on the sheet data
  const processSheetData = async () => {
    if (!fileData) {
      addProgressMessage("Please upload a file first.", "error");
      return;
    }

    if (
      !soapCredentials ||
      !soapCredentials.org_id ||
      !soapCredentials.access_token
    ) {
      addProgressMessage("Please provide SOAP API credentials.", "error");
      return;
    }

    setLoading(true);
    setProgress([]); // Reset progress messages for a new run

    // Reset all caches in cacheRegistry for a fresh run
    Object.keys(cacheRegistry.current).forEach(cacheName => {
        // Only reset maps, not the _isPopulated flags on dynamic caches
        if (cacheRegistry.current[cacheName] instanceof Map) {
            cacheRegistry.current[cacheName] = new Map();
        }
    });

    addProgressMessage("Starting spreadsheet processing...", "info");

    //Special Holiday and Override handling (these still need to be populated from their respective sheets)
    const allHolidayDefinitions = {};
    const allOverrideDefinitions = {};

    try {
      const sheets = fileData.SheetNames;
      console.log("Sheets:", sheets);

      const exclusionList = [
        "Cover",
        "Revisions",
        "Introduction",
        "Acceptance",
        "Register",
        "Status",
        "Pre-Discovery Questionnaire",
        "Naming Scheme",
        "Service Level Threshold",
        "Contact Center Timers",
        "Users",
        "Prompts",
        "Outbound ANI",
        "Desktop Layouts",
        "Chat Settings",
        "External Data Integrations",
        "Call Flows",
        "Multimedia Profiles",
        "GDF Agent",
        "GDF Intents",
        "GDF Entities",
        "Voice Infrastructure",
        "Additional Notes",
      ];

      const order = [
        "Sites",
        "Skill Definitions",
        "Skill Profiles",
        "Teams",
        "Queues",
        "Address Books",
        "Aux Codes",
        "Holidays",
        "Overrides",
        "Hours of Operation",
        "Desktop Profiles",
        "Entry Points",
        "Global Variables",
        "User Profiles"
      ];
      const orderedSheets = order.filter((sheetName) =>
        sheets.includes(sheetName)
      );

      console.log("Sheets to be processed in order:", orderedSheets);

      for (const sheetName of orderedSheets) {
        const sheet = fileData.Sheets[sheetName];

        // Handle exclusion list for documentation sheets
        if (exclusionList.includes(sheetName)) {
          addProgressMessage(
            `Skipping documentation sheet: ${sheetName}`,
            "info"
          );
          continue; // Skip to the next sheet in orderedSheets
        }

        const jsonDataRaw = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          cellDates: true,
        });

        let columnNames;
        let dataRows;
        let startRowIndexForProgress;

        switch (sheetName) {
          default:
            columnNames = jsonDataRaw[1];
            dataRows = jsonDataRaw.slice(2);
            startRowIndexForProgress = 3;
            break;
        }

        if (!Array.isArray(columnNames) || columnNames.length === 0) {
          addProgressMessage(
            `Error: Column names not found or invalid for sheet: ${sheetName}. Skipping this sheet.`,
            "error"
          );
          continue;
        }

        const formattedData = dataRows
          .map((row) => {
            let obj = {};
            columnNames.forEach((col, colIndex) => {
              if (col) {
                obj[col.toString().trim()] = row[colIndex];
              }
            });
            return obj;
          })
          .filter((obj) => Object.keys(obj).length > 0);

        if (formattedData.length === 0) {
          addProgressMessage(
            `No valid data rows found for sheet: ${sheetName}. Skipping.`,
            "warning"
          );
          continue;
        }

        switch (sheetName) {
          case "Sites":
            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Site sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = ["Site Name", "Media Profile"];
              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required Site data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              const mediaProfile = data["Media Profile"];
              let multimediaProfileName;
              if (mediaProfile === "MULTIMEDIA") {
                multimediaProfileName = "Default_Multimedia_Profile";
              } else {
                multimediaProfileName = "Default_Telephony_Profile";
              }

              // --- Get Multimedia Profile ID using getOrFetchOrCreateAndCache ---
              const fetchMultimediaProfileByName = async (name, org, token, addProgressMessage) => {
                  // getmultimediaList fetches all profiles. We return the array if name is null, or find by name.
                  const allProfiles = await getmultimediaList(null, org, token, addProgressMessage); // Pass null to get all
                  if (Array.isArray(allProfiles)) {
                      return name ? allProfiles.find(p => p.name === name) : allProfiles;
                  }
                  return name ? null : []; // Return null for single, empty array for all if not found
              };
              
              // Since creation of Multimedia Profiles via API is not supported,
              // we pass a placeholder function that logs an error and returns null.
              const createMultimediaProfilePlaceholder = async (payload, orgId, accessToken, addProgressMessage) => {
                  addProgressMessage("Creation of Multimedia Profiles is not supported via API. Skipping.", "error");
                  return null;
              };
              const createMultimediaProfilePayloadBuilder = (name, dataRow) => ({}); // Empty payload as creation is not supported

              let multimediaProfileId = await getOrFetchOrCreateAndCache(
                cacheRegistry,
                "MultimediaProfileCache",
                multimediaProfileName,
                fetchMultimediaProfileByName,
                createMultimediaProfilePlaceholder, // Pass the placeholder function
                createMultimediaProfilePayloadBuilder,
                orgid,
                accesstoken,
                addProgressMessage,
                excelRowIndex,
                "Multimedia Profile",
                {}
              );

              if (!multimediaProfileId) {
                addProgressMessage(
                  `Critical Error: Multimedia Profile "${multimediaProfileName}" could not be found for Site "${data["Site Name"]}" (Excel Row: ${excelRowIndex}). Skipping site creation.`,
                  "error"
                );
                continue;
              }

              // --- Get Site ID using getOrFetchOrCreateAndCache ---
              const siteName = data["Site Name"];
              const fetchSiteByName = async (name, org, token, addProgressMessage) => {
                  const allSites = await getSite(null, org, token, addProgressMessage); // getSite now returns array of sites
                  if (Array.isArray(allSites)) {
                      return name ? allSites.find(s => s.name === name) : allSites;
                  }
                  return name ? null : [];
              };
              const createSitePayloadBuilder = (name, dataRow, args) => ({
                  name: name,
                  active: "TRUE",
                  multimediaProfileId: args.multimediaProfileId,
              });

              try {
                let siteID = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "SiteCache",
                  siteName,
                  fetchSiteByName,
                  createSite,
                  createSitePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Site",
                  data,
                  { multimediaProfileId: multimediaProfileId } // Pass the obtained multimediaProfileId
                );

                if (siteID) {
                  addProgressMessage(
                    `Successfully processed Site "${data["Site Name"]}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Site "${data["Site Name"]}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Unexpected error processing Site "${data["Site Name"]}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;

          case "Skill Definitions":
            for (const [rowIndex, data] of formattedData.entries()) {
                const excelRowIndex = rowIndex + startRowIndexForProgress;
                if (
                    Object.values(data).every(
                        (value) =>
                            value === null || value === undefined || value === ""
                    )
                ) {
                    addProgressMessage(
                        `Skipping empty row in Skill Definitions sheet (Excel Row: ${excelRowIndex})`,
                        "info"
                    );
                    continue;
                }

                const requiredFields = ["Skill Name", "Service Level Threshold", "Type (Text, Proficiency, Boolean, Enum)"];
                if (data["Type (Text, Proficiency, Boolean, Enum)"] === "ENUM") {
                    requiredFields.push("Enum Value(s)");
                }

                const missingFields = requiredFields.filter(
                    (field) =>
                        data[field] === undefined ||
                        data[field] === null ||
                        data[field] === ""
                );

                if (missingFields.length > 0) {
                    addProgressMessage(
                        `Error: Missing required Skill Definition data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                        "error"
                    );
                    continue;
                }

                // Wrapper for getSkillDefinitions to find a specific skill by name
                const fetchSkillByName = async (name, org, token, addProgressMessage) => {
                    // getSkillDefinitions fetches all skills. We return the array if name is null, or find by name.
                    const allSkills = await getSkillDefinitions(null, org, token, addProgressMessage);
                    if (Array.isArray(allSkills)) {
                        return name ? allSkills.find(skill => skill.name === name) : allSkills;
                    }
                    return name ? null : [];
                };

                const createSkillPayloadBuilder = (name, dataRow) => {
                    let enumValues;
                    if (dataRow["Type (Text, Proficiency, Boolean, Enum)"] === "ENUM") {
                        enumValues = String(dataRow["Enum Value(s)"])
                            .split(",")
                            .map((item) => item.trim())
                            .filter(item => item !== '') // Filter out empty strings
                            .map((item) => ({ name: item }));
                    }
                    return {
                        name: name,
                        description: dataRow["Description"] || "",
                        serviceLevelThreshold: dataRow["Service Level Threshold"] || 0, // Assuming default or from sheet
                        active: "TRUE",
                        skillType: dataRow["Type (Text, Proficiency, Boolean, Enum)"],
                        ...(enumValues && enumValues.length > 0 && { enumSkillValues: enumValues }),
                    };
                };

                try {
                    const skillId = await getOrFetchOrCreateAndCache(
                        cacheRegistry,
                        "SkillDefinitionCache",
                        data["Skill Name"],
                        fetchSkillByName,
                        createSkill,
                        createSkillPayloadBuilder,
                        orgid,
                        accesstoken,
                        addProgressMessage,
                        excelRowIndex,
                        "Skill Definition",
                        data
                    );

                    if (skillId) {
                        addProgressMessage(
                            `Successfully processed Skill Definition "${data["Skill Name"]}". (Excel Row: ${excelRowIndex})`,
                            "success"
                        );
                    } else {
                        addProgressMessage(
                            `Failed to process Skill Definition "${data["Skill Name"]}". (Excel Row: ${excelRowIndex})`,
                            "error"
                        );
                    }
                } catch (error) {
                    addProgressMessage(
                        `Unexpected error processing Skill Definition "${data["Skill Name"]}": ${
                            error.message || String(error)
                        } (Excel Row: ${excelRowIndex})`,
                        "error"
                    );
                }
            }
            break;

          case "Skill Profiles":
            const groupedSkillProfilesData = {};

            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Skill Profile sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const skillProfileName = data["Skill Profile Name"];
              const skillDefinitionName = data["Skill Definition"]; // This is the skill definition name from the row
              const skillValueFromSheet = data["Value"];

              if (!skillProfileName || !skillDefinitionName || skillValueFromSheet === undefined || skillValueFromSheet === null) {
                addProgressMessage(
                  `Error: Missing required Skill Profile data in row (Excel Row: ${excelRowIndex}). Missing fields: Skill Profile Name, Skill Definition, or Value. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              // Lookup Skill Definition from cache (this will trigger bulk fetch if not already populated)
              // We need to call getOrFetchOrCreateAndCache for the skill definition itself here
              // to ensure it's in the cache before we try to get it.
              let skillDefinitionId;
              let skillType;
              let enumSkillValues;

              try {
                const fetchedSkillDef = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "SkillDefinitionCache",
                  skillDefinitionName,
                  async (name, org, token, addProgressMessage) => {
                      const allDefs = await getSkillDefinitions(null, org, token, addProgressMessage);
                      if (Array.isArray(allDefs)) {
                          return name ? allDefs.find(def => def.name === name) : allDefs;
                      }
                      return name ? null : [];
                  },
                  createSkill, // Assuming createSkill can create a single skill definition
                  (name, dataRow) => {
                    // This payload builder needs to be generic enough for createSkill
                    // It should mimic the payload structure in the "Skill Definitions" case.
                    let enumVals;
                    if (dataRow["Type (Text, Proficiency, Boolean, Enum)"] === "ENUM") {
                        enumVals = String(dataRow["Enum Value(s)"])
                            .split(",")
                            .map((item) => item.trim())
                            .filter(item => item !== '')
                            .map((item) => ({ name: item }));
                    }
                    return {
                        name: name,
                        description: dataRow["Description"] || "",
                        serviceLevelThreshold: dataRow["Service Level Threshold"] || 0,
                        active: "TRUE",
                        skillType: dataRow["Type (Text, Proficiency, Boolean, Enum)"],
                        ...(enumVals && enumVals.length > 0 && { enumSkillValues: enumVals }),
                    };
                  },
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Skill Definition",
                  // Pass the relevant data for the skill definition from the current row if available
                  // This assumes the skill definition data is available in the same row as the skill profile.
                  // If not, this might need adjustment or a separate pre-processing step for skill definitions.
                  data // Passing the entire row, assuming it contains skill def details
                );

                if (fetchedSkillDef) {
                    const cachedSkillDefEntry = getFromCache(cacheRegistry, "SkillDefinitionCache", skillDefinitionName);
                    skillDefinitionId = fetchedSkillDef; // getOrFetchOrCreateAndCache returns ID
                    skillType = cachedSkillDefEntry?.skillType;
                    enumSkillValues = cachedSkillDefEntry?.enumSkillValues;
                } else {
                    addProgressMessage(
                        `Error: Skill Definition "${skillDefinitionName}" could not be fetched or created for Skill Profile "${skillProfileName}" (Excel Row: ${excelRowIndex}). Skipping.`,
                        "error"
                    );
                    continue;
                }
              } catch (error) {
                addProgressMessage(
                  `Error resolving Skill Definition "${skillDefinitionName}" for Skill Profile "${skillProfileName}": ${error.message || String(error)} (Excel Row: ${excelRowIndex})`,
                  "error"
                );
                continue;
              }

              if (!skillType) {
                addProgressMessage(
                  `Error: Skill Type is undefined for Skill Definition "${skillDefinitionName}" after fetch/cache lookup. Please check API response/caching logic. Skipping.`,
                  "error"
                );
                continue;
              }

              // If it's an ENUM, find the matching enumSkillValueId
              let enumSkillValueIds = [];
              if (skillType === "ENUM") {
                const sheetValues = String(skillValueFromSheet)
                  .split(",")
                  .map((value) => value.trim())
                  .filter((value) => value !== "");

                if (sheetValues.length === 0) {
                  addProgressMessage(
                    `Warning: ENUM skill "${skillDefinitionName}" for Skill Profile "${skillProfileName}" (Excel Row: ${excelRowIndex}) has no specified values. Skipping.`,
                    "warning"
                  );
                  continue;
                }

                let allValuesFound = true;
                for (const sheetValue of sheetValues) {
                  const matchingEnumValue = enumSkillValues?.find(
                    (val) =>
                      String(val.name).toLowerCase() ===
                      String(sheetValue).toLowerCase()
                  );

                  if (matchingEnumValue) {
                    enumSkillValueIds.push(matchingEnumValue.id); // Add the ID to the array
                  } else {
                    addProgressMessage(
                      `Error: ENUM value "${sheetValue}" (from "${skillValueFromSheet}") not found for Skill Definition "${skillDefinitionName}" in cache for Skill Profile "${skillProfileName}" (Excel Row: ${excelRowIndex}). Skipping this skill profile entry.`,
                      "error"
                    );
                    allValuesFound = false;
                    break;
                  }
                }

                if (!allValuesFound) {
                  continue;
                }
              }

              if (!groupedSkillProfilesData[skillProfileName]) {
                groupedSkillProfilesData[skillProfileName] = {
                  skillProfileName: skillProfileName,
                  skillDefinitions: [],
                };
              }

              // Add the skill definition details to the array for the current skill profile
              groupedSkillProfilesData[skillProfileName].skillDefinitions.push({
                skillDefinitionId: skillDefinitionId, // This is the main skill ID
                type: skillType, // The type from the API
                value: skillValueFromSheet, // The raw value from Excel
                enumSkillValueIds: enumSkillValueIds, // Only populated for ENUMs
              });
            }

            for (const skillProfileName in groupedSkillProfilesData) {
              const skillProfileData =
                groupedSkillProfilesData[skillProfileName];
              const { skillDefinitions } = skillProfileData;

              const activeSkills = [];
              const activeEnumSkills = [];

              // Iterate through the skillDefinitions to categorize them and build the correct structure
              for (const def of skillDefinitions) {
                switch (
                  def.type.toUpperCase() // Convert to uppercase for consistent comparison
                ) {
                  case "ENUM":
                    // For ENUM type, iterate through the array of enumSkillValueIds
                    if (
                      def.enumSkillValueIds &&
                      def.enumSkillValueIds.length > 0
                    ) {
                      def.enumSkillValueIds.forEach((id) => {
                        activeEnumSkills.push({ enumSkillValueId: id }); // Push each ID as a separate object
                      });
                    } else {
                      // This case handles if enumSkillValueIds array is empty or not present
                      console.warn(
                        `ENUM skill "${def.skillDefinitionId}" in skill profile "${skillProfileName}" has no valid enumSkillValueIds. Skipping.`
                      );
                      addProgressMessage(
                        `Warning: ENUM skill "${def.skillDefinitionId}" in skill profile "${skillProfileName}" has no valid enumSkillValueIds. Skipping.`,
                        "warning"
                      );
                    }
                    break;
                  case "PROFICIENCY":
                    // For Proficiency type, add skillDefinitionId and proficiencyValue
                    activeSkills.push({
                      skillId: def.skillDefinitionId,
                      proficiencyValue: def.value,
                    });
                    break;
                  case "BOOLEAN":
                    // For Boolean type, add skillDefinitionId and booleanValue
                    activeSkills.push({
                      skillId: def.skillDefinitionId,
                      booleanValue: String(def.value).toUpperCase() === "TRUE", // Convert "TRUE"/"FALSE" to boolean
                    });
                    break;
                  case "TEXT":
                    // For Text type, add skillDefinitionId and textValue
                    activeSkills.push({
                      skillId: def.skillDefinitionId,
                      textValue: String(def.value),
                    });
                    break;
                  default:
                    // Handle unexpected types (optional, but good for robustness)
                    console.warn(
                      `Unknown skill type "${def.type}" encountered for skill ID "${def.skillDefinitionId}". Skipping.`
                    );
                    addProgressMessage(
                      `Warning: Unknown skill type "${def.type}" encountered for skill ID "${def.skillDefinitionId}" in skill profile "${skillProfileName}". Skipping.`,
                      "warning"
                    );
                    break;
                }
              }

              const skillProfilePayload = {
                name: skillProfileName,
                //Only include activeSkill if there are actual skills to send
                ...(activeSkills.length > 0 && { activeSkills: activeSkills }),
                // Only include activeEnumSkills if there are actual enum skills to send
                ...(activeEnumSkills.length > 0 && {
                  activeEnumSkills: activeEnumSkills,
                }),
              }; 

              // --- Create Skill Profile using getOrFetchOrCreateAndCache ---
              const fetchSkillProfileByName = async (name, org, token, addProgressMessage) => {
                // getSkillProfile (getskillprofilesList) fetches all, we need to find by name
                const allSkillProfiles = await getSkillProfile(null, org, token, addProgressMessage);
                if (Array.isArray(allSkillProfiles)) {
                    return name ? allSkillProfiles.find(profile => profile.name === name) : allSkillProfiles;
                }
                return name ? null : [];
              };
              const createSkillProfilePayloadBuilder = (name, dataRow) => skillProfilePayload; // Payload is already built

              try {
                const skillProfileID = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "SkillProfileCache",
                  skillProfileName,
                  fetchSkillProfileByName,
                  createSkillProfile,
                  createSkillProfilePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  // Note: excelRowIndex is not directly available here, use a placeholder or derive if needed
                  // For now, using a generic row index for grouped data
                  `Grouped Data for ${skillProfileName}`,
                  "Skill Profile",
                  skillProfilePayload // Pass data for logging context
                );

                if (skillProfileID) {
                  addProgressMessage(
                    `Successfully processed Skill Profile "${skillProfileName}".`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Skill Profile "${skillProfileName}".`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error processing Skill Profile "${skillProfileName}": ${
                    error.message || String(error)
                  }`,
                  "error"
                );
              }
            }
            break;

          case "Teams":
            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Team sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = [
                "Name",
                "Type",
                "Site"
              ];

              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required Team data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              const mediaProfile = data["Media Profile"];
              let multimediaProfileName;
              if (mediaProfile === "MULTIMEDIA") {
                multimediaProfileName = "Default_Multimedia_Profile";
              } else {
                multimediaProfileName = "Default_Telephony_Profile";
              }

              // --- Get Multimedia Profile ID for Team using getOrFetchOrCreateAndCache ---
              const fetchMultimediaProfileByName = async (name, org, token, addProgressMessage) => {
                  const allProfiles = await getmultimediaList(null, org, token, addProgressMessage); // getmultimediaList now returns array
                  if (Array.isArray(allProfiles)) {
                      return name ? allProfiles.find(p => p.name === name) : allProfiles;
                  }
                  return name ? null : [];
              };
            
              const createMultimediaProfilePlaceholder = async (payload, orgId, accessToken, addProgressMessage) => {
                  addProgressMessage("Creation of Multimedia Profiles is not supported via API. Skipping.", "error");
                  return null;
              };
              const createMultimediaProfilePayloadBuilder = (name, dataRow) => ({}); // Empty payload as creation is not supported

              let multimediaProfileId = await getOrFetchOrCreateAndCache(
                cacheRegistry,
                "MultimediaProfileCache",
                multimediaProfileName,
                fetchMultimediaProfileByName,
                createMultimediaProfilePlaceholder,
                createMultimediaProfilePayloadBuilder,
                orgid,
                accesstoken,
                addProgressMessage,
                excelRowIndex,
                "Multimedia Profile",
                {}
              );

              if (!multimediaProfileId) {
                addProgressMessage(
                  `Warning: Multimedia Profile "${multimediaProfileName}" could not be found for Team "${data["Name"]}" (Excel Row: ${excelRowIndex}). Proceeding without it.`,
                  "warning"
                );
              }

              // --- Get Site ID for Team using getOrFetchOrCreateAndCache ---
              let siteId = null;
              if (data["Site"]) {
                const siteName = data["Site"];
                const fetchSiteByName = async (name, org, token, addProgressMessage) => {
                    const allSites = await getSite(null, org, token, addProgressMessage); // getSite now returns array
                    if (Array.isArray(allSites)) {
                        return name ? allSites.find(s => s.name === name) : allSites;
                    }
                    return name ? null : [];
                };
                const createSitePayloadBuilder = (name, dataRow, args) => ({
                    name: name,
                    active: "TRUE",
                    multimediaProfileId: args.multimediaProfileId,
                });

                siteId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "SiteCache",
                  siteName,
                  fetchSiteByName,
                  createSite,
                  createSitePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Site",
                  data,
                  { multimediaProfileId: multimediaProfileId } // Pass multimediaProfileId to site creation
                );
                if (!siteId) {
                  addProgressMessage(
                    `Warning: Team "${data["Name"]}" references non-existent site "${data["Site"]}". Continuing without site association. (Excel Row: ${excelRowIndex})`,
                    "warning"
                  );
                }
              }

              const teamPayloadBuilder = (name, dataRow, args) => ({
                name: name,
                teamType: dataRow["Type"],
                teamStatus: "IN_SERVICE",
                active: "TRUE",
                siteId: args.siteId,
                multimediaProfileId: args.multimediaProfileId,
              });

              // --- Create Team using getOrFetchOrCreateAndCache ---
              const fetchTeamByName = async (name, org, token, addProgressMessage) => {
                  const allTeams = await getTeam(null, org, token, addProgressMessage); // getTeam now returns array
                  if (Array.isArray(allTeams)) {
                      return name ? allTeams.find(team => team.name === name) : allTeams;
                  }
                  return name ? null : [];
              };

              try {
                let teamID = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "TeamIdCache",
                  data["Name"],
                  fetchTeamByName,
                  createTeam,
                  teamPayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Team",
                  data,
                  { siteId: siteId, multimediaProfileId: multimediaProfileId }
                );

                if (teamID) {
                  addProgressMessage(
                    `Successfully processed Team "${data["Name"]}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Team "${data["Name"]}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error processing Team "${data["Name"]}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;
            case "Queues":
              const groupedQueuesData = {};
  
              // --- First Pass: Grouping data by Queue Name ---
              for (const [rowIndex, data] of formattedData.entries()) {
                const excelRowIndex = rowIndex + startRowIndexForProgress;
                const queueName = data["Name"];
  
                if (
                  Object.values(data).every(
                    (value) =>
                      value === null || value === undefined || value === ""
                  )
                ) {
                  addProgressMessage(
                    `Skipping empty row in Queue sheet (Excel Row: ${excelRowIndex})`,
                    "info"
                  );
                  continue;
                }
  
                // Initialize the queue entry if it's the first time we see this queue name
                if (!groupedQueuesData[queueName]) {
                  // Store common queue properties from the first encountered row
                  groupedQueuesData[queueName] = {
                    dataRow: { ...data }, // Keep a copy of the first data row for common properties
                    excelRowIndex: excelRowIndex, // Store the Excel row index of the first entry for logging
                    skillAssignments: [], // Array to collect all skill requirements for this queue
                    callDistributionGroups: [], // Array to collect all team assignments for this queue
                  };
                }
  
                // Define required fields for a Queue (checked per row for initial validation)
                const requiredFields = [
                  "Name",
                  "Direction",
                  "Channel Type",
                  "Service Level Threshold",
                  "Max Time in Queue",
                  "Routing Type"
                ];
  
                const missingFields = requiredFields.filter(
                  (field) =>
                    data[field] === undefined ||
                    data[field] === null ||
                    data[field] === ""
                );
  
                if (missingFields.length > 0) {
                  addProgressMessage(
                    `Error: Missing required Queue data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                    "error"
                  );
                  continue; // Skip this row, but continue processing other rows for the same queue if they exist
                }
  
                if (data["Routing Type"] === "Longest Available") {
                  const agentAssignmentString = data["Call Distribution Group (Team(s))"];
                  if (!agentAssignmentString || agentAssignmentString === undefined || agentAssignmentString === null) {
                    addProgressMessage(
                      `Error: Missing required Call Distribution Group data for Queue "${queueName}" in row (Excel Row: ${excelRowIndex}). Missing fields: Call Distribution Group (Team(s)). Data: ${JSON.stringify(data)}`,
                      "error"
                    );
                  }
                  continue;
                }
  
                // Collect Skill Assignment details if Routing Type is "Skill Based" for this row
                // The condition `!agentAssignmentString` here seems to imply that skill-based routing is exclusive of team-based routing in the same row.
                // If a queue can have both, you might need to adjust this condition.
                if (data["Routing Type"] === "Skill Based") { // Removed `!agentAssignmentString` as it might not be needed for skill assignment collection
                  const skillDefinitionName = data["Skill Assignment Definition"];
                  const skillCondition = data["Skill Assignment Condition"];
                  const skillValue = data["Skill Assignment Value (Numerical/Text/Boolean)"];
  
                  if (!skillDefinitionName || !skillCondition || skillValue === undefined || skillValue === null) {
                    addProgressMessage(
                      `Error: Missing required Skill Assignment data for Queue "${queueName}" in row (Excel Row: ${excelRowIndex}). Missing fields: Skill Assignment Definition, Condition, or Value. Data: ${JSON.stringify(data)}`,
                      "error"
                    );
                    // Do not continue here, as other rows for the same queue might be valid.
                    // This skill assignment simply won't be added.
                  } else {
                    groupedQueuesData[queueName].skillAssignments.push({
                      skillDefinitionName: skillDefinitionName,
                      skillCondition: skillCondition,
                      skillValue: skillValue,
                      excelRowIndex: excelRowIndex, // Keep original row index for specific skill errors
                    });
                  }
                }
              }
  
              // --- Second Pass: Process each grouped Queue entry ---
              for (const queueName in groupedQueuesData) {
                const queueGroup = groupedQueuesData[queueName];
                const data = queueGroup.dataRow; // Use the data from the first row for common properties
                const excelRowIndex = queueGroup.excelRowIndex; // Use the representative row index
  
                // --- Get Default Cisco MOH File as placeholder ---
                const mohFiles = await getAudioFiles(orgid, accesstoken, addProgressMessage);
                let defaultMusicFileId = null;
                if (mohFiles && Array.isArray(mohFiles)) {
                    const defaultMohFile = mohFiles.find(file => 
                        file.name && String(file.name).toLowerCase().includes("defaultmusic")
                    );
                    if (defaultMohFile) {
                        defaultMusicFileId = defaultMohFile.id;
                        addProgressMessage(`Found default music on hold file containing "defaultmusic" with name "${defaultMohFile.name}" and ID: ${defaultMusicFileId}.`, "info");
                    } else {
                        addProgressMessage(`Warning: Default music on hold file containing "defaultmusic" not found for Queue "${queueName}". Queue might use a different default.`, "warning");
                    }
                } else {
                    addProgressMessage(`Warning: Could not fetch audio files for Queue "${queueName}". Default music on hold file will not be set.`, "warning");
                }
  
                // --- Process Skill Requirements (now from grouped data) ---
                const queueSkillRequirements = [];
                if (data["Routing Type"] === "Skill Based" && queueGroup.skillAssignments.length > 0) {
                  for (const skillAssignment of queueGroup.skillAssignments) {
                    const skillDefinitionName = skillAssignment.skillDefinitionName;
                    const skillCondition = skillAssignment.skillCondition;
                    const skillValue = skillAssignment.skillValue;
                    const skillExcelRowIndex = skillAssignment.excelRowIndex; // Use the specific row index for skill error
  
                    const cachedSkillDefinitionEntry = getFromCache(cacheRegistry, "SkillDefinitionCache", skillDefinitionName);
  
                    if (cachedSkillDefinitionEntry && cachedSkillDefinitionEntry.id && cachedSkillDefinitionEntry.skillType) {
                      const skillType = String(cachedSkillDefinitionEntry.skillType).toUpperCase();
                      const normalizedCondition = String(skillCondition);
  
                      // --- Skill Condition Validation based on Skill Type ---
                      let isValidCondition = true;
                      let apiCondition;
  
                      switch (skillType) {
                          case "BOOLEAN":
                          case "ENUM":
                              if (normalizedCondition === "Is") {
                                  apiCondition = "IS";
                              } else {
                                  isValidCondition = false;
                                  addProgressMessage(
                                      `Error: Invalid Skill Assignment Condition "${skillCondition}" for "${skillType}" skill "${skillDefinitionName}" in Queue "${queueName}" (Excel Row: ${skillExcelRowIndex}). Only "Is" is allowed. Skipping this skill assignment.`,
                                      "error"
                                  );
                              }
                              break;
                          case "TEXT":
                              if (normalizedCondition === "Is") {
                                  apiCondition = "IS";
                              } else if (normalizedCondition === "Is Not") {
                                  apiCondition = "IS_NOT";
                              } else {
                                  isValidCondition = false;
                                  addProgressMessage(
                                      `Error: Invalid Skill Assignment Condition "${skillCondition}" for "${skillType}" skill "${skillDefinitionName}" in Queue "${queueName}" (Excel Row: ${skillExcelRowIndex}). Only "Is" or "Is Not" are allowed. Skipping this skill assignment.`,
                                      "error"
                                  );
                              }
                              break;
                          case "PROFICIENCY":
                              if (normalizedCondition === "Is") {
                                  apiCondition = "IS";
                              } else if (normalizedCondition === ">=") {
                                  apiCondition = "GTE";
                              } else if (normalizedCondition === "<=") {
                                  apiCondition = "LTE";
                              } else {
                                  isValidCondition = false;
                                  addProgressMessage(
                                      `Error: Invalid Skill Assignment Condition "${skillCondition}" for "${skillType}" skill "${skillDefinitionName}" in Queue "${queueName}" (Excel Row: ${skillExcelRowIndex}). Only "Is", ">=", or "<=" are allowed. Skipping this skill assignment.`,
                                      "error"
                                  );
                              }
                              break;
                          default:
                              isValidCondition = false;
                              addProgressMessage(
                                  `Error: Unknown Skill Type "${skillType}" for skill "${skillDefinitionName}" in Queue "${queueName}" (Excel Row: ${skillExcelRowIndex}). Skipping this skill assignment.`,
                                  "error"
                              );
                              break;
                      }
  
                      if (!isValidCondition) {
                          continue; // Skip this skill assignment if the condition is invalid
                      }
  
                      // Determine the value based on skill type
                      let skillPayloadValue;
                      switch (skillType) { // Use the already normalized skillType
                          case "PROFICIENCY":
                              skillPayloadValue = parseInt(skillValue, 10);
                              if (isNaN(skillPayloadValue)) {
                                  addProgressMessage(
                                      `Error: Invalid Proficiency Value "${skillValue}" for skill "${skillDefinitionName}" in Queue "${queueName}" (Excel Row: ${skillExcelRowIndex}). Skipping this skill assignment.`,
                                      "error"
                                  );
                                  continue; // Skip this specific skill assignment
                              }
                              break;
                          case "BOOLEAN":
                              skillPayloadValue = String(skillValue).toUpperCase() === "TRUE";
                              break;
                          case "ENUM":
                              // For ENUM, skillValue should be the name of the enum value, we need its ID
                              const matchingEnumValue = cachedSkillDefinitionEntry.enumSkillValues?.find(
                                  (val) => String(val.name).toLowerCase() === String(skillValue).toLowerCase()
                              );
                              if (matchingEnumValue) {
                                  skillPayloadValue = matchingEnumValue.id; // Use the ID of the enum value
                              } else {
                                  addProgressMessage(
                                      `Error: ENUM value "${skillValue}" not found for Skill Definition "${skillDefinitionName}" in Queue "${queueName}" (Excel Row: ${skillExcelRowIndex}). Skipping this skill assignment.`,
                                      "error"
                                  );
                                  continue; // Skip this specific skill assignment
                              }
                              break;
                          case "TEXT":
                          default: // This will catch any remaining unknown types but should be handled by validation above
                              skillPayloadValue = String(skillValue);
                              break;
                      }
  
                      queueSkillRequirements.push({
                        skillId: cachedSkillDefinitionEntry.id,
                        skillType: cachedSkillDefinitionEntry.skillType, // Pass the API type
                        condition: apiCondition,
                        value: skillPayloadValue,
                        priority: 1 // Default priority, adjust if sheet has a column for this
                      });
                    } else {
                      addProgressMessage(
                        `Warning: Skill Definition "${skillDefinitionName}" not found in cache for Queue "${queueName}" (Excel Row: ${skillExcelRowIndex}). Please ensure Skill Definitions are processed first. Skipping this skill assignment.`,
                        "warning"
                      );
                    }
                  }
                }
  
                // --- Process Call Distribution Groups (Teams) ---
                const callDistributionGroups = [];
                const agentAssignmentString = data["Call Distribution Group (Team(s))"];
                if (agentAssignmentString) {
                  const teamNames = agentAssignmentString.split(',').map(t => t.trim()).filter(t => t !== '');
                  const agentGroups = [];
  
                  const fetchTeamByName = async (name, org, token, addProgressMessage) => {
                      const allTeams = await getTeam(null, org, token, addProgressMessage);
                      if (Array.isArray(allTeams)) {
                          return name ? allTeams.find(team => team.name === name) : allTeams;
                      }
                      return name ? null : [];
                  };
  
                  const createTeamPayloadBuilder = (name, dataRow) => ({
                      name: name,
                      description: dataRow["Team Description"] || "",
                  });
  
                  for (const teamName of teamNames) {
                    const teamId = await getOrFetchOrCreateAndCache(
                      cacheRegistry,
                      "TeamIdCache",
                      teamName,
                      fetchTeamByName,
                      createTeam,
                      createTeamPayloadBuilder,
                      orgid,
                      accesstoken,
                      addProgressMessage,
                      excelRowIndex,
                      "Team",
                      data
                    );
  
                    if (teamId) {
                      agentGroups.push({ teamId: teamId });
                    } else {
                      addProgressMessage(
                        `Warning: Team "${teamName}" could not be found or created for Queue "${queueName}" (Excel Row: ${excelRowIndex}). Skipping this team.`,
                        "warning"
                      );
                    }
                  }
                  if (agentGroups.length > 0) {
                      callDistributionGroups.push({
                          agentGroups: agentGroups,
                          order: 1,
                          duration: 0
                      });
                  }
                }
  
                // Determine routingType based on the Excel data
                let resolvedRoutingType = "SKILLS_BASED"; // Default value
                if (data["Routing Type"] === "Longest Available") {
                  resolvedRoutingType = "LONGEST_AVAILABLE_AGENT";
                }
  
                // Determine skillBasedRoutingType with the new conditional logic
                let resolvedSkillBasedRoutingType = "BEST_AVAILABLE_AGENT"; // Default value
                if (resolvedRoutingType === "LONGEST_AVAILABLE_AGENT") {
                  resolvedSkillBasedRoutingType = null; // Set to null as per "set to blank" request
                } else if (data["Skill Based Routing Type"] === "Longest Available") {
                  resolvedSkillBasedRoutingType = "LONGEST_AVAILABLE_AGENT";
                } else if (data["Skill Based Routing Type"] === "Best Available") {
                  resolvedSkillBasedRoutingType = "BEST_AVAILABLE_AGENT";
                }
                
                let resolvedQueueRoutingType = "SKILL_BASED"
                if (data["Routing Type"] === "Longest Available") {
                  resolvedQueueRoutingType = "TEAM_BASED";
                }

  
                // --- Construct the full Queue Payload ---
                const QueuePayload = {
                  name: data["Name"],
                  description: data["Description"] || "",
                  queueType: data["Direction"],
                  checkAgentAvailability: false,
                  channelType: data["Channel Type"],
                  serviceLevelThreshold: data["Service Level Threshold"],
                  maxActiveContacts: data["Max Active Contacts"] || 0,
                  maxTimeInQueue: data["Max Time in Queue"],
                  ...(defaultMusicFileId && { defaultMusicInQueueMediaFileId: defaultMusicFileId }),
                  active: true,
                  outdialCampaignEnabled: (data["Direction"] === "OUTBOUND"),
                  monitoringPermitted: data["Monitoring Permitted"] || true,
                  parkingPermitted: data["Parking Permitted"] || false,
                  recordingPermitted: data["Recording Permitted"] || false,
                  recordingAllCallsPermitted: data["Recording All Calls Permitted"] || false,
                  pauseRecordingPermitted: data["Pause Recording Permitted"] || false,
                  recordingPauseDuration: data["Recording Pause Duration"] || 10,
                  routingType: resolvedRoutingType,
                  ...(resolvedSkillBasedRoutingType !== null && { skillBasedRoutingType: resolvedSkillBasedRoutingType }),
                  ...(resolvedQueueRoutingType !== null && { queueRoutingType: resolvedQueueRoutingType}),
                  // Only include queueSkillRequirements if routingType is SKILLS_BASED and there are skills
                  ...(resolvedRoutingType === "SKILLS_BASED" && queueSkillRequirements.length > 0 && { queueSkillRequirements: queueSkillRequirements }),
                  
                  callDistributionGroups: callDistributionGroups,
                  controlFlowScriptUrl: "http://localhost:9000",
                  ivrRequeueUrl: "http://localhost:9000"
                };
  
                // --- Create the Queue using modular cache ---
                const createQueuePayloadBuilder = (name, dataRow) => QueuePayload;
  
                const fetchQueueByName = async (name, org, token, addProgressMessage) => {
          
                     const allQueues = await getqueueList(null,org, token, addProgressMessage);
                     if (Array.isArray(allQueues)) {
                         return name ? allQueues.find(q => q.name === name) : allQueues;
                     }
                    return name ? null : [];
                };
  
                try {
                  let QueueID = await getOrFetchOrCreateAndCache(
                    cacheRegistry,
                    "QueueIdCache",
                    queueName, // Use the grouped queue name
                    fetchQueueByName,
                    async (payload, orgId, accessToken, addProgressMessage) => {
                        await createQueue(payload, orgId, accessToken, addProgressMessage);
                        return payload.name;
                    },
                    createQueuePayloadBuilder,
                    orgid,
                    accesstoken,
                    addProgressMessage,
                    excelRowIndex,
                    "Queue",
                    data
                  );
  
                  if (QueueID) {
                    addProgressMessage(
                      `Successfully processed Queue "${queueName}". (Excel Row: ${excelRowIndex})`,
                      "success"
                    );
                  } else {
                    addProgressMessage(
                      `Failed to process Queue "${queueName}". (Excel Row: ${excelRowIndex})`,
                      "error"
                    );
                  }
                } catch (error) {
                  addProgressMessage(
                    `Unexpected error processing Queue "${queueName}": ${
                      error.message || String(error)
                    } (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              }
              break;

          case "Address Books":
            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              const addressBookName = data["Address Book Name"];
              const addressBookEntryName = data["Address Book Entry Name"];
              const addressBookEntryNumber = data["Address Book Entry Number"];

              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Address Book sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = ["Address Book Name", "Address Book Type"];
              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required Address Book data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              // Determine Parent Type
              const parentType = String(data["Address Book Type"]).toUpperCase();

              // Get Site ID if Site Name is provided
              let siteId = null;
              if (data["Site Name"]) {
                const siteName = data["Site Name"];
                const fetchSiteByName = async (name, org, token, addProgressMessage) => {
                    const allSites = await getSite(null, org, token, addProgressMessage); // getSite now returns array
                    if (Array.isArray(allSites)) {
                        return name ? allSites.find(s => s.name === name) : allSites;
                    }
                    return name ? null : [];
                };
                const createSitePayloadBuilder = (name, dataRow, args) => ({
                    name: name,
                    active: "TRUE",
                    // multimediaProfileId: args.multimediaProfileId, // Not needed for Address Book's site creation
                });

                siteId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "SiteCache",
                  siteName,
                  fetchSiteByName,
                  createSite,
                  createSitePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Site",
                  data,
                  {} // No multimediaProfileId needed here
                );
                if (!siteId) {
                  addProgressMessage(
                    `Warning: Address Book "${addressBookName}" references non-existent site "${data["Site Name"]}". Continuing without site association. (Excel Row: ${excelRowIndex})`,
                    "warning"
                  );
                }
              }

              const fetchAddressBookByName = async (name, org, token, addProgressMessage) => {
                  const allAddressBooks = await getAddressBook(null, org, token, addProgressMessage); // getAddressBook now returns array
                  if (Array.isArray(allAddressBooks)) {
                      return name ? allAddressBooks.find(ab => ab.name === name) : allAddressBooks;
                  }
                  return name ? null : [];
              };

              const createAddressBookPayloadBuilder = (name, dataRow, args) => ({
                name: name,
                description: dataRow["Address Book Description"] || "",
                parentType: args.parentType,
                ...(args.parentType === "SITE" && { siteId: args.siteId }),
              });

              try {
                const currentAddressBookId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "AddressBookCache",
                  addressBookName,
                  fetchAddressBookByName,
                  createAddressBook,
                  createAddressBookPayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Address Book",
                  data,
                  { parentType: parentType, siteId: siteId }
                );

                if (currentAddressBookId) {
                  // Logic to add entries to the address book (if applicable)
                  const abrequiredEntryFields = [
                    "Address Book Entry Name",
                    "Address Book Entry Number",
                  ];

                  if (
                    abrequiredEntryFields.some(
                      (field) =>
                        data[field] === undefined ||
                        data[field] === null ||
                        data[field] === ""
                    )
                  ) {
                    addProgressMessage(
                      `Error: Missing required Address Book Entry data in row (Excel Row: ${excelRowIndex}). Skipping entry creation. ${JSON.stringify(data)}`,
                      "error"
                    );
                  } else {
                    // NEW: Use getOrFetchOrCreateAndCache for Address Book Entries
                    const addressBookEntryCacheName = `AddressBookEntries_${currentAddressBookId}`;
                    // Ensure this dynamic cache is initialized
                    if (!cacheRegistry.current[addressBookEntryCacheName]) {
                        cacheRegistry.current[addressBookEntryCacheName] = new Map();
                    }

                    const fetchAddressBookEntryByName = async (name, org, token, addProgressMessage, args) => {
                        // Pass the addressBookId to the new API script
                        const allEntries = await getaddressbookEntries(args.addressBookId, org, token, addProgressMessage);
                        if (Array.isArray(allEntries)) {
                            // Populate the cache for this address book's entries
                            allEntries.forEach(entry => {
                                addToCache(cacheRegistry, addressBookEntryCacheName, entry.name, entry);
                            });
                            return name ? allEntries.find(entry => entry.name === name) : allEntries;
                        }
                        return name ? null : [];
                    };

                    const createAddressBookEntryPayloadBuilder = (name, dataRow) => ({
                        name: name,
                        number: dataRow["Address Book Entry Number"],
                    });

                    try {
                      const entryId = await getOrFetchOrCreateAndCache(
                        cacheRegistry,
                        addressBookEntryCacheName, // Use dynamic cache name
                        addressBookEntryName,
                        fetchAddressBookEntryByName,
                        // createAddressBookEntry needs to be adapted to accept payload and return ID
                        // For now, it's called directly below, but for consistency, it should be integrated.
                        // If createAddressBookEntry cannot return an ID or doesn't fit the pattern,
                        // you might need a wrapper or a different strategy for entries.
                        async (payload, orgId, accessToken, addProgressMessage) => {
                            // This is a wrapper to fit createAddressBookEntry into the getOrFetchOrCreateAndCache pattern
                            await createAddressBookEntry(currentAddressBookId, orgId, payload, accessToken, addProgressMessage);
                            // Assuming successful creation, we might not get an ID back directly.
                            // If the API returns an ID, return it here. Otherwise, return a placeholder.
                            // For now, we'll return the name as a pseudo-ID if the API doesn't return one.
                            return payload.name; // Or a real ID if createAddressBookEntry returns it
                        },
                        createAddressBookEntryPayloadBuilder,
                        orgid,
                        accesstoken,
                        addProgressMessage,
                        excelRowIndex,
                        "Address Book Entry",
                        data,
                        { addressBookId: currentAddressBookId } // Pass addressBookId to fetcher
                      );

                      if (entryId) {
                        addProgressMessage(
                          `Successfully processed entry "${addressBookEntryName}" for Address Book "${addressBookName}". (Excel Row: ${excelRowIndex})`,
                          "success"
                        );
                      } else {
                        addProgressMessage(
                          `Failed to process entry "${addressBookEntryName}" for Address Book "${addressBookName}". (Excel Row: ${excelRowIndex})`,
                          "error"
                        );
                      }
                    } catch (error) {
                      addProgressMessage(
                        `Error processing Address Book Entry "${addressBookEntryName}" for "${addressBookName}": ${error.message || String(error)} (Excel Row: ${excelRowIndex})`,
                        "error"
                      );
                    }
                  }
                  addProgressMessage(
                    `Successfully processed Address Book "${addressBookName}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Address Book "${addressBookName}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Unexpected error processing Address Book "${addressBookName}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;
          case "Aux Codes":
            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Idle Codes sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = ["Code Name", "Type"];

              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required Idle Codes data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              // Aux Codes are a bit tricky because getWorkTypes returns IDs, not entities by name.
              // For simplicity, we'll fetch work types once and pass them as additionalArgs.
              // If your API allows fetching individual Aux Codes by name, you'd use that for `fetchAuxCodeByName`.
              let idleCodeWorkTypeId = null;
              let wraupupWorktTypeId = null;
              try {
                // getWorkTypes is called directly here, not through getOrFetchOrCreateAndCache,
                // as it's a utility to get IDs for Aux Codes rather than a primary entity to cache.
                const workTypes = await getWorkTypes(null,
                  orgid,
                  accesstoken,
                  addProgressMessage
                );
                const idleWorkType = workTypes.find(wt => wt.name === "Default Idle Work Type");
                const wrapupWorkType = workTypes.find(wt => wt.name === "Default Wrapup Work Type");

                console.log("idleWorkType ", idleWorkType);
                console.log("wrapupWorkType ", wrapupWorkType);
                idleCodeWorkTypeId = idleWorkType ? idleWorkType.id : null;
                wraupupWorktTypeId = wrapupWorkType ? wrapupWorkType.id : null;

                if (!idleCodeWorkTypeId || !wraupupWorktTypeId) {
                    addProgressMessage(
                        `Warning: Could not find required Work Types (Idle/Wrapup) for Aux Code "${data["Code Name"]}". (Excel Row: ${excelRowIndex})`,
                        "warning"
                    );
                }

              } catch (error) {
                addProgressMessage(
                  `Error fetching Work Types for Aux Code "${data["Code Name"]}": ${error.message || String(error)} (Excel Row: ${excelRowIndex}). Skipping.`,
                  "error"
                );
                continue;
              }

              // Define fetcher and creator for Aux Code
              const fetchAuxCodeByName = async (name, org, token, addProgressMessage) => {
                  const allAuxCodes = await getAuxCodes(null, org, token, addProgressMessage); // Use the corrected getAuxCodes
                  if (Array.isArray(allAuxCodes)) {
                      return name ? allAuxCodes.find(ac => ac.name === name) : allAuxCodes;
                  }
                  return name ? null : [];
              };

              const createAuxCodePayloadBuilder = (name, dataRow, args) => ({
                // Pass work type IDs from additionalArgs
                idleCodeWorkTypeId: args.idleCodeWorkTypeId,
                wraupupWorktTypeId: args.wraupupWorktTypeId,
                name: name,
                type: dataRow["Type"]
              });

              try {
                const auxCodeId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "AuxCodeCache",
                  data["Code Name"],
                  fetchAuxCodeByName,
                  // Wrapper for createAuxCode to ensure it returns an ID
                  async (payload, orgId, accessToken, addProgressMessage) => {
                      await createAuxCode(payload, orgId, accessToken, addProgressMessage);
                      return payload.name; // Or the actual ID if createAuxCode returns it
                  },
                  createAuxCodePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Aux Code",
                  data,
                  { idleCodeWorkTypeId: idleCodeWorkTypeId, wraupupWorktTypeId: wraupupWorktTypeId }
                );

                if (auxCodeId) {
                  addProgressMessage(
                    `Successfully processed Aux Code "${data["Code Name"]}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Aux Code "${data["Code Name"]}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error processing Aux Code "${data["Code Name"]}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;

          // Case for "Holidays" sheet - This case primarily *parses* data into allHolidayDefinitions
          // The actual creation of Holiday Lists happens in "Hours of Operation" if referenced.
          // However, if you want to create Holiday Lists directly from this sheet, you can add that logic here.
          case "Holidays":
            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Holidays sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const holidayListName = data["Holiday List Name"];
              const holidayName = data["Holiday"];
              const holidayDate = data["Date"]; // Assuming date is in a format like 'MM/DD/YY' or Excel Date number

              if (!holidayListName || !holidayName || !holidayDate) {
                addProgressMessage(
                  `Error: Missing data in Holidays sheet row (Excel Row: ${excelRowIndex}). Skipping.`,
                  "error"
                );
                continue;
              }

              if (!allHolidayDefinitions[holidayListName]) {
                allHolidayDefinitions[holidayListName] = [];
              }

              const formattedHolidayDate = convertExcelDate(holidayDate);

              allHolidayDefinitions[holidayListName].push({
                name: holidayName,
                startDate: formattedHolidayDate,
                endDate: formattedHolidayDate,
              });
            }
            addProgressMessage(
              `Successfully parsed ${
                Object.keys(allHolidayDefinitions).length
              } Holiday Lists from sheet.`,
              "success"
            );
            // If you want to create Holiday Lists directly from this sheet, add a loop here:
            for (const holidayListName in allHolidayDefinitions) {
                const excelRowIndexForList = 0; // Or find a representative row index
                const fetchHolidayListByName = async (name, org, token, addProgressMessage) => {
                    const allHolidayLists = await getholidayList(null, org, token, addProgressMessage); // getholidayList now returns array
                    if (Array.isArray(allHolidayLists)) {
                        return name ? allHolidayLists.find(hl => hl.name === name) : allHolidayLists;
                    }
                    return name ? null : [];
                };
                const createHolidayListPayloadBuilder = (name, dataRow, args) => ({
                    name: name,
                    holidays: args.definitions[name] || [],
                });

                try {
                    const holidayListId = await getOrFetchOrCreateAndCache(
                        cacheRegistry,
                        "HolidayListCache",
                        holidayListName,
                        fetchHolidayListByName,
                        createHolidayList,
                        createHolidayListPayloadBuilder,
                        orgid,
                        accesstoken,
                        addProgressMessage,
                        excelRowIndexForList, // Use a representative row index
                        "Holiday List",
                        {}, // No specific dataRow needed from this context
                        { definitions: allHolidayDefinitions }
                    );
                    if (holidayListId) {
                        addProgressMessage(`Successfully processed Holiday List "${holidayListName}".`, "success");
                    } else {
                        addProgressMessage(`Failed to process Holiday List "${holidayListName}".`, "error");
                    }
                } catch (error) {
                    addProgressMessage(`Error processing Holiday List "${holidayListName}": ${error.message || String(error)}`, "error");
                }
            }
            break;

          // Case for "Overrides" sheet - Similar to Holidays, this parses data and can create lists
          case "Overrides":
            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Overrides sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const overrideListName = data["Override List Name"]; // Assuming this column exists
              const overrideName = data["Override Name"]; // Assuming this is the name of the individual override entry

              if (!overrideListName || !overrideName) { // Check for overrideType
                addProgressMessage(
                  `Error: Missing data in Overrides sheet row (Excel Row: ${excelRowIndex}). Skipping.`,
                  "error"
                );
                continue;
              }

              if (!allOverrideDefinitions[overrideListName]) {
                allOverrideDefinitions[overrideListName] = [];
              }

              const formattedOverrideDate = getFormattedCurrentDateYYYYMMDD();

              allOverrideDefinitions[overrideListName].push({
                name: overrideName,
                startDateTime: formattedOverrideDate + "T00:00", 
                endDateTime: formattedOverrideDate + "T23:59",
                workingHours: false, // Default or from sheet
              });
            }
            addProgressMessage(
              `Successfully parsed ${
                Object.keys(allOverrideDefinitions).length
              } Override Lists from sheet.`,
              "success"
            );
            // Loop to create Override Lists based on parsed definitions
            for (const overrideListName in allOverrideDefinitions) {
                const excelRowIndexForList = 0; // Or find a representative row index
                const fetchOverridesListByName = async (name, org, token, addProgressMessage) => {
                    const allOverridesLists = await getoverrides(null, org, token, addProgressMessage); // getoverrides now returns array
                    if (Array.isArray(allOverridesLists)) {
                        return name ? allOverridesLists.find(ol => ol.name === name) : allOverridesLists;
                    }
                    return name ? null : [];
                };
                const createOverridesListPayloadBuilder = (name, dataRow, args) => ({
                    name: name,
                    timezone: timezone,
                    overrides: args.definitions[name] || [],
                });

                try {
                    const overrideListId = await getOrFetchOrCreateAndCache(
                        cacheRegistry,
                        "OverridesCache",
                        overrideListName,
                        fetchOverridesListByName,
                        createOverrideList,
                        createOverridesListPayloadBuilder,
                        orgid,
                        accesstoken,
                        addProgressMessage,
                        excelRowIndexForList,
                        "Overrides List",
                        {},
                        { definitions: allOverrideDefinitions }
                    );
                    if (overrideListId) {
                        addProgressMessage(`Successfully processed Overrides List "${overrideListName}".`, "success");
                    } else {
                        addProgressMessage(`Failed to process Overrides List "${overrideListName}".`, "error");
                    }
                } catch (error) {
                    addProgressMessage(`Error processing Overrides List "${overrideListName}": ${error.message || String(error)}`, "error");
                }
            }
            break;

          case "Hours of Operation":
            // NEW: Fetch all existing Business Hours and populate cache at the start of the sheet processing
            try {
                // Pass null for the first argument to get all business hours
                const allExistingBusinessHours = await getBusinessHours(null, orgid, accesstoken, addProgressMessage);
                if (Array.isArray(allExistingBusinessHours)) {
                    allExistingBusinessHours.forEach(bh => {
                        addToCache(cacheRegistry, "BusinessHoursCache", bh.name, bh);
                    });
                    addProgressMessage(`Successfully pre-fetched ${allExistingBusinessHours.length} existing Business Hours into cache.`, "info");
                } else {
                    addProgressMessage(`No existing Business Hours found or response was not an array.`, "info");
                }
            } catch (error) {
                addProgressMessage(`Error pre-fetching existing Business Hours: ${error.message || String(error)}`, "error");
            }

            const groupedHoOData = {};

            // Step 1: Group data by "Schedule Name"
            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Hours of Operation sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const scheduleName = data["Schedule Name"];
              if (!scheduleName) {
                addProgressMessage(
                  `Error: "Schedule Name" is missing in row (Excel Row: ${excelRowIndex}). Skipping.`,
                  "error"
                );
                continue;
              }

              if (!groupedHoOData[scheduleName]) {
                groupedHoOData[scheduleName] = {
                  description: data["Description"], // Assuming description is consistent per schedule name
                  timeZone: data["Time Zone"], // Assuming timezone is consistent per schedule name
                  days: [],
                  holidayName: data["Holidays"], // Name of the Holiday List
                  overrideName: data["Overrides"], // Name of the Overrides List
                };
              }

              // Convert times for the current day entry
              const startTimeConverted = convertExcelTime(data["Start Time"]);
              const endTimeConverted = convertExcelTime(data["End Time"]);

              // If Day of Week Start Time or End Time are blank, not a valid time, or explicitly "Closed",
              // then skip this day entry entirely from the workingHours payload.
              if (
                !startTimeConverted ||
                !endTimeConverted ||
                startTimeConverted === "Closed" ||
                endTimeConverted === "Closed"
              ) {
                addProgressMessage(
                  `Skipping ${
                    data["Day of Week"]
                  } for schedule "${scheduleName}" due to blank, invalid, or 'Closed' time entries. (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue; // Skip this day entry
              }

              groupedHoOData[scheduleName].days.push({
                dayOfWeek: data["Day of Week"],
                startTime: startTimeConverted,
                endTime: endTimeConverted,
              });
            }

            // Step 2: Process each grouped HoO entry
            for (const scheduleName in groupedHoOData) {
              const hooSchedules = groupedHoOData[scheduleName];
              const { description, timeZone, days, holidayName, overrideName } =
                hooSchedules;
              const excelRowIndexForHoO = 0; // Use a representative row index for grouped HoO data

              let currentHolidayId = null;
              let currentOverrideId = null;

              // --- Get Holiday List ID using modular cache ---
              if (holidayName) {
                const fetchHolidayListByName = async (name, org, token, addProgressMessage) => {
                    const allHolidayLists = await getholidayList(null, org, token, addProgressMessage); // getholidayList now returns array
                    if (Array.isArray(allHolidayLists)) {
                        return name ? allHolidayLists.find(hl => hl.name === name) : allHolidayLists;
                    }
                    return name ? null : [];
                };
                const createHolidayListPayloadBuilder = (name, dataRow, args) => ({
                    name: name,
                    holidays: allHolidayDefinitions[name] || [], // Use pre-parsed definitions
                });

                currentHolidayId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "HolidayListCache",
                  holidayName,
                  fetchHolidayListByName,
                  createHolidayList,
                  createHolidayListPayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndexForHoO,
                  "Holiday List",
                  {},
                  { definitions: allHolidayDefinitions }
                );
                if (!currentHolidayId) {
                  addProgressMessage(
                    `Warning: Holiday List "${holidayName}" not found or could not be created for HoO "${scheduleName}". (Excel Row: ${excelRowIndexForHoO})`,
                    "warning"
                  );
                }
              }

              // --- Get Override List ID using modular cache ---
              if (overrideName) {
                const fetchOverridesListByName = async (name, org, token, addProgressMessage) => {
                    const allOverridesLists = await getoverrides(null, org, token, addProgressMessage); // getoverrides now returns array
                    if (Array.isArray(allOverridesLists)) {
                        return name ? allOverridesLists.find(ol => ol.name === name) : allOverridesLists;
                    }
                    return name ? null : [];
                };
                const createOverridesListPayloadBuilder = (name, dataRow, args) => ({
                    name: name,
                    timezone: timezone,
                    overrides: allOverrideDefinitions[name] || [],
                });

                try {
                    const overrideListId = await getOrFetchOrCreateAndCache(
                        cacheRegistry,
                        "OverridesCache",
                        overrideName,
                        fetchOverridesListByName,
                        createOverrideList,
                        createOverridesListPayloadBuilder,
                        orgid,
                        accesstoken,
                        addProgressMessage,
                        excelRowIndexForHoO,
                        "Overrides List",
                        {},
                        { definitions: allOverrideDefinitions }
                    );
                    if (overrideListId) {
                        addProgressMessage(`Successfully processed Overrides List "${overrideName}".`, "success");
                    } else {
                        addProgressMessage(`Failed to process Overrides List "${overrideName}".`, "error");
                    }
                } catch (error) {
                    addProgressMessage(`Error processing Overrides List "${overrideName}": ${error.message || String(error)}`, "error");
                }
            }
            // End of Override List creation loop
            
              // Build workingHours array
              const workingHoursMap = new Map(); // Key: "startTime-endTime-name", Value: array of days
              const daysOfWeekOrder = [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ];

              for (const dayEntry of hooSchedules.days) {
                const { dayOfWeek, startTime, endTime } = dayEntry;
                // Determine the 'name' for the working hour block (e.g., "Open" or "Closed")
                const blockName =
                  startTime === "Closed" && endTime === "Closed"
                    ? "Closed"
                    : "Open";
                const key = `${startTime}-${endTime}-${blockName}`;

                if (!workingHoursMap.has(key)) {
                  workingHoursMap.set(key, {
                    days: [],
                    name: blockName,
                    startTime: blockName === "Open" ? startTime : undefined,
                    endTime: blockName === "Open" ? endTime : undefined,
                  });
                }
                // Convert full day name to uppercase abbreviation
                const dayAbbr = dayOfWeek.substring(0, 3).toUpperCase();
                workingHoursMap.get(key).days.push(dayAbbr);
              }

              // Sort working hours blocks by the order of their first day in the week
              const workingHours = Array.from(workingHoursMap.values()).sort(
                (a, b) => {
                  const firstDayAIndex = daysOfWeekOrder.indexOf(
                    daysOfWeekOrder.find(
                      (day) => day.substring(0, 3).toUpperCase() === a.days[0]
                    )
                  );
                  const firstDayBIndex = daysOfWeekOrder.indexOf(
                    daysOfWeekOrder.find(
                      (day) => day.substring(0, 3).toUpperCase() === b.days[0]
                    )
                  );
                  return firstDayAIndex - firstDayBIndex;
                }
              );

              const businessHoursPayloadBuilder = (name, dataRow, args) => ({
                name: name,
                description: args.description || `${name} Business Hours`,
                timezone: timezone,
                workingHours: args.workingHours,
                holidaysId: args.holidaysId,
                overridesId: args.overridesId,
              });

              // --- Create Business Hours using getOrFetchOrCreateAndCache ---
              const fetchBusinessHoursByName = async (name, org, token, addProgressMessage) => {
                  const allBusinessHours = await getBusinessHours(null, org, token, addProgressMessage); // getBusinessHours now returns array
                  if (Array.isArray(allBusinessHours)) {
                      return name ? allBusinessHours.find(bh => bh.name === name) : allBusinessHours;
                  }
                  return name ? null : []; // Placeholder if no getBusinessHours API exists
              };

              try {
                const businessHoursId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "BusinessHoursCache",
                  scheduleName,
                  fetchBusinessHoursByName,
                  createBusinessHours,
                  businessHoursPayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndexForHoO,
                  "Business Hours",
                  {}, // No direct dataRow needed for payload builder, args are used
                  {
                    description: description,
                    workingHours: workingHours,
                    holidaysId: currentHolidayId,
                    overridesId: currentOverrideId,
                  }
                );
                if (businessHoursId) {
                  addProgressMessage(
                    `Successfully processed Business Hours for "${scheduleName}".`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Business Hours for "${scheduleName}".`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error creating Business Hours "${scheduleName}": ${
                    error.message || String(error)
                  }`,
                  "error"
                );
              }
            }
            break; // End of "Hours of Operation" case
          case "Desktop Profiles":
            // NEW: Fetch all existing Desktop Profiles and populate cache at the start of the sheet processing
            try {
                const allExistingDesktopProfiles = await getDesktopProfiles(null, orgid, accesstoken, addProgressMessage);
                if (Array.isArray(allExistingDesktopProfiles)) {
                    allExistingDesktopProfiles.forEach(dp => {
                        addToCache(cacheRegistry, "DesktopProfileCache", dp.name, dp);
                    });
                    addProgressMessage(`Successfully pre-fetched ${allExistingDesktopProfiles.length} existing Desktop Profiles into cache.`, "info");
                } else {
                    addProgressMessage(`No existing Desktop Profiles found or response was not an array.`, "info");
                }
            } catch (error) {
                addProgressMessage(`Error pre-fetching existing Desktop Profiles: ${error.message || String(error)}`, "error");
            }

            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Desktop Profiles sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = [
                "Name",
                "Type",
                "Screen Pops",
                "Last Agent Routing",
                "Auto Answer",
                "Wrap-Up Type",
                "If Auto, Time (Seconds)",
              ];

              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required Desktop Profiles data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              // Determine if autoWrapup is true based on the Excel data
              let wrapuptimer;
              const isAutoWrapup = data["Wrap-Up Type"] === "AUTO";
              if (isAutoWrapup) {
                wrapuptimer =
                  parseInt(data["If Auto, Time (Seconds)"], 10) * 1000;
              }

              //Determine Parent Type
              const parentType = String(data["Type"].toUpperCase());

              // Get site ID if Site Name is provided
              let siteId = null;
              if (data["Site Name"]) {
                const siteName = data["Site Name"];
                const fetchSiteByName = async (name, org, token, addProgressMessage) => {
                    const allSites = await getSite(null, org, token, addProgressMessage); // getSite now returns array
                    if (Array.isArray(allSites)) {
                        return name ? allSites.find(s => s.name === name) : allSites;
                    }
                    return name ? null : [];
                };
                const createSitePayloadBuilder = (name, dataRow, args) => ({
                    name: name,
                    active: "TRUE",
                    // multimediaProfileId: args.multimediaProfileId, // Not needed for Desktop Profile's site creation
                });

                siteId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "SiteCache",
                  siteName,
                  fetchSiteByName,
                  createSite,
                  createSitePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Site",
                  data,
                  {} // No multimediaProfileId needed here
                );
                if (!siteId) {
                  addProgressMessage(
                    `Warning: Desktop Profile "${data["Name"]}" references non-existent site "${data["Site Name"]}". Continuing without site association. (Excel Row: ${excelRowIndex})`,
                    "warning"
                  );
                }
              }

              const desktopProfilePayloadBuilder = (name, dataRow, args) => ({
                name: name,
                parentType: args.parentType,
                ...(args.parentType === "SITE" && { siteId: args.siteId }),
                screenPopup: dataRow["Screen Pops"] === "TRUE",
                lastAgentRouting: dataRow["Last Agent Routing"] === "TRUE",
                autoAnswer: dataRow["Auto Answer"] === "TRUE",
                autoWrapUp: args.isAutoWrapup,
                ...(args.isAutoWrapup && { autoWrapAfterSeconds: args.wrapuptimer }),
                accessWrapUpCode: "ALL",
                accessIdleCode: "ALL",
                accessQueue: "ALL",
                accessEntryPoint: "ALL",
                accessBuddyTeam: "ALL",
                agentDNValidation: "NONE",
                viewableStatistics: {
                  accessQueueStats: "ALL",
                  accessTeamStats: "ALL",
                },
                loginVoiceOptions: ["AGENT_DN", "EXTENSION", "BROWSER"],
                active: "TRUE",
              });

              // --- Create Desktop Profile using getOrFetchOrCreateAndCache ---
              const fetchDesktopProfileByName = async (name, org, token, addProgressMessage) => {
                  const allDesktopProfiles = await getDesktopProfiles(null, org, token, addProgressMessage); // getDesktopProfiles now returns array
                  if (Array.isArray(allDesktopProfiles)) {
                      return name ? allDesktopProfiles.find(dp => dp.name === name) : allDesktopProfiles;
                  }
                  return name ? null : []; // Placeholder if no getDesktopProfiles API exists
              };

              try {
                const desktopProfileId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "DesktopProfileCache", // Assuming you add this to cacheRegistry
                  data["Name"],
                  fetchDesktopProfileByName,
                  createDesktopProfile,
                  desktopProfilePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Desktop Profile",
                  data,
                  { parentType: parentType, siteId: siteId, isAutoWrapup: isAutoWrapup, wrapuptimer: wrapuptimer }
                );

                if (desktopProfileId) {
                  addProgressMessage(
                    `Successfully processed Desktop Profile "${data["Name"]}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Desktop Profile "${data["Name"]}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error processing Desktop Profiles "${data["Name"]}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;
          case "Entry Points":
            // NEW: Fetch all existing Entry Points and populate cache at the start of the sheet processing
            try {
                const allExistingEntryPoints = await getEntryPoint(null,orgid, accesstoken, addProgressMessage);
                if (Array.isArray(allExistingEntryPoints)) {
                    allExistingEntryPoints.forEach(ep => {
                        addToCache(cacheRegistry, "EntryPointCache", ep.name, ep);
                    });
                    addProgressMessage(`Successfully pre-fetched ${allExistingEntryPoints.length} existing Entry Points into cache.`, "info");
                } else {
                    addProgressMessage(`No existing Entry Points found or response was not an array.`, "info");
                }
            } catch (error) {
                addProgressMessage(`Error pre-fetching existing Entry Points: ${error.message || String(error)}`, "error");
            }

            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Entry Points sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = [
                "Name",
                "Direction",
                "Channel",
                "Service Level Threshold",
              ];

              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required Entry Points data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              if (
                data["Channel"] === "CHAT" ||
                data["Channel"] === "Chat" ||
                data["Channel"] === "EMAIL" ||
                data["Channel"] === "Email"
              ) {
                addProgressMessage(
                  `Skipping Entry Point with Channel "${
                    data["Channel"]
                  }" in row (Excel Row: ${excelRowIndex}).`,
                  "info"
                );
                continue; // Skip to the next row
              }

              const entrypointPayloadBuilder = (name, dataRow) => ({
                name: name,
                entryPointType: dataRow["Direction"],
                channelType: data["Channel"],
                serviceLevelThreshold: data["Service Level Threshold"],
                active: "TRUE",
                maximumActiveContacts: 0,
              });

              // --- Create Entry Point using getOrFetchOrCreateAndCache ---
              const fetchEntryPointByName = async (name, org, token, addProgressMessage) => {
                  const allEntryPoints = await getEntryPoint(null, org, token, addProgressMessage); // getEntryPoints now returns array
                  if (Array.isArray(allEntryPoints)) {
                      return name ? allEntryPoints.find(ep => ep.name === name) : allEntryPoints;
                  }
                  return name ? null : []; // Placeholder if no getEntryPoints API exists
              };

              try {
                const entryPointId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "EntryPointCache", // Assuming you add this to cacheRegistry
                  data["Name"],
                  fetchEntryPointByName,
                  createEntryPoint,
                  entrypointPayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Entry Point",
                  data
                );

                if (entryPointId) {
                  addProgressMessage(
                    `Successfully processed Entry Point "${data["Name"]}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Entry Point "${data["Name"]}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error processing Entry Points "${data["Name"]}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;
            case "Global Variables": // NEW: Case for Global Variables
            // Pre-fetch existing CAD variables
            try {
                const allExistingCADVariables = await getCADvariable(null,orgid, accesstoken, addProgressMessage);
                if (Array.isArray(allExistingCADVariables)) {
                    allExistingCADVariables.forEach(cadVar => {
                        addToCache(cacheRegistry, "CADVariableCache", cadVar.name, cadVar);
                    });
                    addProgressMessage(`Successfully pre-fetched ${allExistingCADVariables.length} existing Global Variables into cache.`, "info");
                } else {
                    addProgressMessage(`No existing Global Variables found or response was not an array.`, "info");
                }
            } catch (error) {
                addProgressMessage(`Error pre-fetching existing Global Variables: ${error.message || String(error)}`, "error");
            }

            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in Global Variables sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = ["Name", "Type", "Default Value", "Agent Viewable","Reportable"];

              let agview = String(data["Agent Viewable"]).toUpperCase();
              let agedit = String(data["Agent Viewable"]).toUpperCase();
              let report = String(data["Agent Viewable"]).toUpperCase();
              // If Agent Viewable is TRUE, Desktop Label is required
              if (agview === "TRUE") {
                requiredFields.push("Desktop Label");
                requiredFields.push("Agent Editable");
                agview = true;
              } else {
                agview = false;
              }

              if (agedit === "TRUE") {
                agedit = true;
              } else {
                agedit = false;
              }
              
              if (report === "TRUE") {
                report = true;
              } else {  
                report = false;
              }

              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required Global Variable data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              const variableName = String(data["Name"]);
              const variableType = String(data["Type"]);
              let defaultValue = data["Default Value"];
              let agentViewable = agview;
              const agentEditable = agedit;
              const desktopLabel = data["Desktop Label"] || null; // Will be checked by requiredFields if agentViewable is true
              const reportable = report;

              if (agentEditable === "TRUE" && agentViewable === "FALSE"){
                addProgressMessage(
                  `Error: Agent Editable cannot be True if Agent Viewable is False (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              // --- Data Type Validation and Formatting ---
              let isValidValue = true;
              switch (String(variableType).toUpperCase()) {
                case "STRING":
                  defaultValue = String(defaultValue);
                  break;
                case "INTEGER":
                  defaultValue = defaultValue;
                  if (isNaN(defaultValue)) {
                    isValidValue = false;
                    addProgressMessage(
                      `Error: Invalid Integer value "${data["Default Value"]}" for Global Variable "${variableName}" (Excel Row: ${excelRowIndex}).`,
                      "error"
                    );
                  }
                  break;
                case "DATETIME":
                   let convertdate = convertExcelDate(data["Default Value"]) + " 12:00:00";
                   let date = new Date(convertdate);
                   defaultValue = date.getTime()/1000.0;
                   console.log("DateTime",defaultValue);
                  /*if (!isNaN(defaultValue)){
                    isValidValue = false;
                    addProgressMessage(
                      `Error: Invalid Date Time value "${defaultValue}" for Global Variable "${variableName}" (Excel Row: ${excelRowIndex}). Expected a valid date/time format.`,
                      "error"
                    );
                  } */
                  break;
                case "BOOLEAN":
                  const normalizedBoolean = String(defaultValue).toUpperCase();
                  if (normalizedBoolean === "TRUE" || normalizedBoolean === "FALSE") {
                    defaultValue = String(normalizedBoolean) === "TRUE";
                  } else {
                    isValidValue = false;
                    addProgressMessage(
                      `Error: Invalid Boolean value "${data["Default Value"]}" for Global Variable "${variableName}" (Excel Row: ${excelRowIndex}). Expected "TRUE" or "FALSE".`,
                      "error"
                    );
                  }
                  break;
                case "DECIMAL":
                  defaultValue = parseFloat(defaultValue);
                  if (isNaN(defaultValue)) {
                    isValidValue = false;
                    addProgressMessage(
                      `Error: Invalid Decimal value "${data["Default Value"]}" for Global Variable "${variableName}" (Excel Row: ${excelRowIndex}).`,
                      "error"
                    );
                  } else {
                    // Format to ensure decimal representation: if integer, add ".0"
                    if (Number.isInteger(defaultValue)) {
                        defaultValue = defaultValue.toFixed(1); // e.g., 123 -> "123.0"
                    } else {
                        defaultValue = defaultValue; // Keep as is, e.g., 0.X
                    }
                  }
                  break;
                default:
                  isValidValue = false;
                  addProgressMessage(
                    `Error: Unknown Type "${data["Type"]}" for Global Variable "${variableName}" (Excel Row: ${excelRowIndex}).`,
                    "error"
                  );
                  break;
              }

              if (!isValidValue) {
                continue; // Skip this row if value is invalid
              }

              const cadVariablePayloadBuilder = (name, dataRow) => ({
                name: variableName,
                variableType: variableType,
                active: true,
                defaultValue: defaultValue,
                agentEditable: agentEditable,
                agentViewable: agentViewable,
                ...(agentViewable && { desktopLabel: desktopLabel }), // Only include if agentViewable is true
                reportable: reportable
              });

              const fetchCADVariableByName = async (name, org, token, addProgressMessage) => {
                  const allCADVars = await getCADvariable(null, org, token, addProgressMessage);
                  if (Array.isArray(allCADVars)) {
                      return name ? allCADVars.find(v => v.name === name) : allCADVars;
                  }
                  return name ? null : [];
              };

              try {
                const cadVariableId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "CADVariableCache",
                  variableName,
                  fetchCADVariableByName,
                  createCADVariable,
                  cadVariablePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "Global Variable",
                  data
                );

                if (cadVariableId) {
                  addProgressMessage(
                    `Successfully processed Global Variable "${variableName}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process Global Variable "${variableName}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error processing Global Variable "${variableName}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;
            case "User Profiles": // NEW: Case for User Profiles
            // Pre-fetch existing User Profiles
            try {
                const allExistingUserProfiles = await getuserProfiles(null,orgid, accesstoken, addProgressMessage);
                if (Array.isArray(allExistingUserProfiles)) {
                    allExistingUserProfiles.forEach(userProfile => {
                        addToCache(cacheRegistry, "UserProfileCache", userProfile.name, userProfile);
                    });
                    addProgressMessage(`Successfully pre-fetched ${allExistingUserProfiles.length} existing User Profiles into cache.`, "info");
                } else {
                    addProgressMessage(`No existing User Profiles found or response was not an array.`, "info");
                }
            } catch (error) {
                addProgressMessage(`Error pre-fetching existing User Profiles: ${error.message || String(error)}`, "error");
            }

            for (const [rowIndex, data] of formattedData.entries()) {
              const excelRowIndex = rowIndex + startRowIndexForProgress;
              if (
                Object.values(data).every(
                  (value) =>
                    value === null || value === undefined || value === ""
                )
              ) {
                addProgressMessage(
                  `Skipping empty row in User Profiles sheet (Excel Row: ${excelRowIndex})`,
                  "info"
                );
                continue;
              }

              const requiredFields = [
                "Name",
                "Profile Type"
              ];

              const profileName = String(data["Name"]);
              const profileType = String(data["Profile Type"]).toUpperCase();

              // Conditional required fields based on Profile Type
              if (profileType !== "ADMINISTRATOR" && profileType !== "ADMINISTRATOR_ONLY") {
                requiredFields.push("Entry Point Access");
                requiredFields.push("Site Access");
                requiredFields.push("Queue Access");
                requiredFields.push("Team Access");
              }

              // Conditional required fields based on Access Types
              if (String(data["Entry Point Access"]).toUpperCase() === "SPECIFIC") {
                requiredFields.push("Entry Points");
              }
              if (String(data["Site Access"]).toUpperCase() === "SPECIFIC") {
                requiredFields.push("Sites");
              }
              if (String(data["Queue Access"]).toUpperCase() === "SPECIFIC") {
                requiredFields.push("Queues");
              }
              if (String(data["Team Access"]).toUpperCase() === "SPECIFIC") {
                requiredFields.push("Teams");
              }

              const missingFields = requiredFields.filter(
                (field) =>
                  data[field] === undefined ||
                  data[field] === null ||
                  data[field] === ""
              );

              if (missingFields.length > 0) {
                addProgressMessage(
                  `Error: Missing required User Profile data in row (Excel Row: ${excelRowIndex}). Missing fields: ${missingFields.join(", ")}. Data: ${JSON.stringify(data)}`,
                  "error"
                );
                continue;
              }

              let entryPointIds = [];
              let siteIds = [];
              let queueIds = [];
              let teamIds = [];

              // If not Administrator, fetch related IDs
              if (profileType !== "ADMINISTRATOR" && profileType !== "ADMINISTRATOR_ONLY") {

                // --- Process SPECIFIC access types ---
                if (String(data["Entry Point Access"]).toUpperCase() === "SPECIFIC" && data["Entry Points"]) {
                    const epNames = String(data["Entry Points"]).split(',').map(name => name.trim()).filter(name => name !== '');
                    const fetchEntryPointByName = async (name, org, token, addProgressMessage) => {
                        const allEntryPoints = await getEntryPoint(null, org, token, addProgressMessage);
                        if (Array.isArray(allEntryPoints)) {
                            return name ? allEntryPoints.find(ep => ep.name === name) : allEntryPoints;
                        }
                        return name ? null : [];
                    };
                    const createEntryPointPlaceholder = async (payload, orgId, accessToken, addProgressMessage) => {
                        addProgressMessage(`Entry Point "${payload.name}" not found. Creation of Entry Points must be handled in the 'Entry Points' sheet. Skipping.`, "error");
                        return null;
                    };
                    const createEntryPointPayloadBuilder = (name, dataRow) => ({});

                    for (const epName of epNames) {
                        const id = await getOrFetchOrCreateAndCache(
                            cacheRegistry,
                            "EntryPointCache",
                            epName,
                            fetchEntryPointByName,
                            createEntryPointPlaceholder,
                            createEntryPointPayloadBuilder,
                            orgid,
                            accesstoken,
                            addProgressMessage,
                            excelRowIndex,
                            "Entry Point",
                            data
                        );
                        if (id) {
                            entryPointIds.push(id);
                        } else {
                            addProgressMessage(`Warning: Entry Point "${epName}" not found for User Profile "${profileName}". Skipping. (Excel Row: ${excelRowIndex})`, "warning");
                        }
                    }
                }

                if (String(data["Site Access"]).toUpperCase() === "SPECIFIC" && data["Sites"]) {
                    const sNames = String(data["Sites"]).split(',').map(name => name.trim()).filter(name => name !== '');
                    const fetchSiteByNameSpecific = async (name, org, token, addProgressMessage) => {
                        const allSites = await getSite(null, org, token, addProgressMessage);
                        if (Array.isArray(allSites)) {
                            return name ? allSites.find(s => s.name === name) : allSites;
                        }
                        return name ? null : [];
                    };
                    const createSitePlaceholderSpecific = async (payload, orgId, accessToken, addProgressMessage) => {
                        addProgressMessage(`Site "${payload.name}" not found. Creation of Sites must be handled in the 'Sites' sheet. Skipping.`, "error");
                        return null;
                    };
                    const createSitePayloadBuilderSpecific = (name, dataRow) => ({});

                    for (const sName of sNames) {
                        const id = await getOrFetchOrCreateAndCache(
                            cacheRegistry,
                            "SiteCache",
                            sName,
                            fetchSiteByNameSpecific,
                            createSitePlaceholderSpecific,
                            createSitePayloadBuilderSpecific,
                            orgid,
                            accesstoken,
                            addProgressMessage,
                            excelRowIndex,
                            "Site",
                            data
                        );
                        if (id) {
                            siteIds.push(id);
                        } else {
                            addProgressMessage(`Warning: Site "${sName}" not found for User Profile "${profileName}". Skipping. (Excel Row: ${excelRowIndex})`, "warning");
                        }
                    }
                }

                if (String(data["Queue Access"]).toUpperCase() === "SPECIFIC" && data["Queues"]) {
                    const qNames = String(data["Queues"]).split(',').map(name => name.trim()).filter(name => name !== '');
                    const fetchQueueByNameSpecific = async (name, org, token, addProgressMessage) => {
                        const allQueues = await getqueueList(null,org, token, addProgressMessage); // Assuming getQueues fetches all
                        if (Array.isArray(allQueues)) {
                            return name ? allQueues.find(q => q.name === name) : allQueues;
                        }
                        return name ? null : [];
                    };
                    const createQueuePlaceholderSpecific = async (payload, orgId, accessToken, addProgressMessage) => {
                        addProgressMessage(`Queue "${payload.name}" not found. Creation of Queues must be handled in the 'Queues' sheet. Skipping.`, "error");
                        return null;
                    };
                    const createQueuePayloadBuilderSpecific = (name, dataRow) => ({});

                    for (const qName of qNames) {
                        const id = await getOrFetchOrCreateAndCache(
                            cacheRegistry,
                            "QueueIdCache",
                            qName,
                            fetchQueueByNameSpecific,
                            createQueuePlaceholderSpecific,
                            createQueuePayloadBuilderSpecific,
                            orgid,
                            accesstoken,
                            addProgressMessage,
                            excelRowIndex,
                            "Queue",
                            data
                        );
                        if (id) {
                            queueIds.push(id);
                        } else {
                            addProgressMessage(`Warning: Queue "${qName}" not found for User Profile "${profileName}". Skipping. (Excel Row: ${excelRowIndex})`, "warning");
                        }
                    }
                }

                if (String(data["Team Access"]).toUpperCase() === "SPECIFIC" && data["Teams"]) {
                    const tNames = String(data["Teams"]).split(',').map(name => name.trim()).filter(name => name !== '');
                    const fetchTeamByNameSpecific = async (name, org, token, addProgressMessage) => {
                        const allTeams = await getTeam(null, org, token, addProgressMessage);
                        if (Array.isArray(allTeams)) {
                            return name ? allTeams.find(team => team.name === name) : allTeams;
                        }
                        return name ? null : [];
                    };
                    const createTeamPlaceholderSpecific = async (payload, orgId, accessToken, addProgressMessage) => {
                        addProgressMessage(`Team "${payload.name}" not found. Creation of Teams must be handled in the 'Teams' sheet. Skipping.`, "error");
                        return null;
                    };
                    const createTeamPayloadBuilderSpecific = (name, dataRow) => ({});

                    for (const tName of tNames) {
                        const id = await getOrFetchOrCreateAndCache(
                            cacheRegistry,
                            "TeamIdCache",
                            tName,
                            fetchTeamByNameSpecific,
                            createTeamPlaceholderSpecific,
                            createTeamPayloadBuilderSpecific,
                            orgid,
                            accesstoken,
                            addProgressMessage,
                            excelRowIndex,
                            "Team",
                            data
                        );
                        if (id) {
                            teamIds.push(id);
                        } else {
                            addProgressMessage(`Warning: Team "${tName}" not found for User Profile "${profileName}". Skipping. (Excel Row: ${excelRowIndex})`, "warning");
                        }
                    }
                }
              }

              // --- Construct User Profile Payload ---
              const userProfilePayloadBuilder = (name, dataRow) => {
                const payload = {
                    name: name,
                    profileType: profileType,
                    active: true,
                    accessAllModules: "ALL",
                    accessAllEntryPoints:"ALL",
                    accessAllSites:"ALL",
                    accessAllQueues:"ALL",
                    accessAllTeams:"ALL"
                };

                // Add fields only if not Administrator profile
                if (profileType !== "ADMINISTRATOR") {
                  payload.accessAllModules = "SPECIFIC"

                  if (profileType === "ADMINISTRATOR_ONLY") {
                    payload.accessAllModules = "SPECIFIC";
                    payload.moduleAccessTyp = "EDIT";
                    payload.userProfileAppModules = [
                      {
                        "appModuleId": "m_agent_personal_greetings",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_agent_wellbeing",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_auto_csat",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_call_recording",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_functions",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_generated_summaries",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_imi_digital_channels",
                        "moduleAccessType": "NONE",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_manage_digital_settings",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_manage_tenant_settings",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_provisioning",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_manage_address_book",
                            "appFeatureName": "Manage Address Books",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_holiday_list",
                            "appFeatureName": "Manage Holiday List",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_sites",
                            "appFeatureName": "Manage Sites",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_tenants",
                            "appFeatureName": "Manage Tenants",
                            "featureAccessType": "OFF"
                          },
                          {
                            "appFeatureId": "f_manage_worktype",
                            "appFeatureName": "Manage WorkTypes",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_threshold_rule",
                            "appFeatureName": "Manage Threshold Rules",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_users",
                            "appFeatureName": "Manage Users",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_entrypt_queue",
                            "appFeatureName": "Manage Entry Points/Queues",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_desktop_layout",
                            "appFeatureName": "Manage Desktop Layouts",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_user_profiles",
                            "appFeatureName": "Manage User Profiles",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_portal_branding",
                            "appFeatureName": "Portal Branding",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_dial_plans",
                            "appFeatureName": "Manage Dial Plans",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_revoke_api_key",
                            "appFeatureName": "Revoke API Key",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_dn_mappings",
                            "appFeatureName": "DN Mapping",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_overrides",
                            "appFeatureName": "Manage Overrides",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_audit_trail",
                            "appFeatureName": "Audit Trail",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_agent_profile",
                            "appFeatureName": "Manage Agent Profiles",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_auxiliary_code",
                            "appFeatureName": "Manage Auxiliary Codes",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_skill",
                            "appFeatureName": "Manage Skills",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_teams",
                            "appFeatureName": "Manage Teams",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_skill_profile",
                            "appFeatureName": "Manage Skill Profiles",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_global_variables",
                            "appFeatureName": "Manage Global Variables",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_multimedia_profile",
                            "appFeatureName": "Manage Multimedia Profiles",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_business_hours",
                            "appFeatureName": "Manage Business Hours",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_outdial_ani",
                            "appFeatureName": "Manage Outdial ANIs",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_realtime_transcripts",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_routing_strategy",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_manage_call_flow_scripts",
                            "appFeatureName": "Manage Call Flow Scripts",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_media_files",
                            "appFeatureName": "Manage Media Files",
                            "featureAccessType": "ON"
                          }
                        ]
                      }
                    ];

                  }

                  if (profileType === "SUPERVISOR") {
                    payload.accessAllEntryPoints = String(data["Entry Point Access"]);
                    payload.accessAllSites = String(data["Site Access"]);
                    payload.accessAllQueues = String(data["Queue Access"]);
                    payload.accessAllTeams = String(data["Team Access"]);
                    payload.accessAllModules = "SPECIFIC";
                    payload.moduleAccessType = "EDIT";
                    payload.userProfileAppModules = [
                      {
                        "appModuleId": "m_additional_supervisory_features",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_change_agent_states",
                            "appFeatureName": "Change Agent States",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_agent_queue_assignments",
                            "appFeatureName": "Manage Agent Queue Assignments",
                            "featureAccessType": "OFF"
                          },
                          {
                            "appFeatureId": "f_send_messages",
                            "appFeatureName": "Send Messages",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_sign_out_agents",
                            "appFeatureName": "Sign Out Agents",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_reskill_agents",
                            "appFeatureName": "Reskill Agents",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_agent_desktop",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_agent_personal_greetings",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_call_monitoring",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_barge_in",
                            "appFeatureName": "Barge-In",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_view_blind_monitor_requests",
                            "appFeatureName": "View Blind Monitor Requests",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_mid_call_monitor",
                            "appFeatureName": "Mid-Call Monitor",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_restricted_monitor_only",
                            "appFeatureName": "Restricted Monitor Only",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_whisper_coach",
                            "appFeatureName": "Whisper Coach",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_call_recording",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_campaign_manager",
                        "moduleAccessType": "NONE",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_functions",
                        "moduleAccessType": "NONE",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_imi_digital_channels",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_logout_agents",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_manage_agent_states",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_sign_out_agent",
                            "appFeatureName": "Sign Out Agent",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_change_agent_state",
                            "appFeatureName": "Change Agent State",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_multimedia",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_mm_agent_desktop",
                            "appFeatureName": "MM Agent Desktop",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_mm_provisioning",
                            "appFeatureName": "MM Provisioning",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_mm_basic_digital",
                            "appFeatureName": "Basic Digital",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_mm_social_channel",
                            "appFeatureName": "Social Channel",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_provisioning",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_manage_dial_plans",
                            "appFeatureName": "Manage Dial Plans",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_revoke_api_key",
                            "appFeatureName": "Revoke API Key",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_business_hours",
                            "appFeatureName": "Manage Business Hours",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_users",
                            "appFeatureName": "Manage Users",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_multimedia_profile",
                            "appFeatureName": "Manage Multimedia Profiles",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_audit_trail",
                            "appFeatureName": "Audit Trail",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_user_profiles",
                            "appFeatureName": "Manage User Profiles",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_outdial_ani",
                            "appFeatureName": "Manage Outdial ANIs",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_skill_profile",
                            "appFeatureName": "Manage Skill Profiles",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_global_variables",
                            "appFeatureName": "Manage Global Variables",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_skill",
                            "appFeatureName": "Manage Skills",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_worktype",
                            "appFeatureName": "Manage WorkTypes",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_overrides",
                            "appFeatureName": "Manage Overrides",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_desktop_layout",
                            "appFeatureName": "Manage Desktop Layouts",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_entrypt_queue",
                            "appFeatureName": "Manage Entry Points/Queues",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_auxiliary_code",
                            "appFeatureName": "Manage Auxiliary Codes",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_threshold_rule",
                            "appFeatureName": "Manage Threshold Rules",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_tenants",
                            "appFeatureName": "Manage Tenants",
                            "featureAccessType": "OFF"
                          },
                          {
                            "appFeatureId": "f_manage_address_book",
                            "appFeatureName": "Manage Address Books",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_sites",
                            "appFeatureName": "Manage Sites",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_teams",
                            "appFeatureName": "Manage Teams",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_holiday_list",
                            "appFeatureName": "Manage Holiday List",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_portal_branding",
                            "appFeatureName": "Portal Branding",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_dn_mappings",
                            "appFeatureName": "DN Mapping",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_agent_profile",
                            "appFeatureName": "Manage Agent Profiles",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_real_time_reports",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_agent_state_change",
                            "appFeatureName": "Agent State Change",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_summary_view",
                            "appFeatureName": "Summary View",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_realtime_threshold_alerts",
                            "appFeatureName": "Real-time Threshold Alerts",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_web_callback_report",
                            "appFeatureName": "Web Callback Report",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_recording_management",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_tags",
                            "appFeatureName": "Tags",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_reporting_analytics",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_analyzer_data_exchange",
                            "appFeatureName": "Analyzer Data Exchange",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_business_rules",
                            "appFeatureName": "Business Rules",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_routing_strategy",
                        "moduleAccessType": "EDIT",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_manage_media_files",
                            "appFeatureName": "Manage Media Files",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_manage_call_flow_scripts",
                            "appFeatureName": "Manage Call Flow Scripts",
                            "featureAccessType": "ON"
                          }
                        ]
                      },
                      {
                        "appModuleId": "m_workforce_optimization",
                        "moduleAccessType": "NONE",
                        "userProfileAppFeature": []
                      }
                    ];
                  
                  }

                  if (profileType === "STANDARD_AGENT") {
                    payload.accessAllEntryPoints = String(data["Entry Point Access"]);
                    payload.accessAllSites = String(data["Site Access"]);
                    payload.accessAllQueues = String(data["Queue Access"]);
                    payload.accessAllTeams = String(data["Team Access"]);
                    payload.accessAllModules = "SPECIFIC";
                    payload.userProfileAppModules = [
                      {
                        "appModuleId": "m_agent_desktop",
                        "moduleAccessType": "NONE",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_multimedia",
                        "moduleAccessType": "VIEW",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_mm_basic_digital",
                            "appFeatureName": "Basic Digital",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_mm_social_channel",
                            "appFeatureName": "Social Channel",
                            "featureAccessType": "OFF"
                          },
                          {
                            "appFeatureId": "f_mm_agent_desktop",
                            "appFeatureName": "MM Agent Desktop",
                            "featureAccessType": "OFF"
                          },
                          {
                            "appFeatureId": "f_mm_provisioning",
                            "appFeatureName": "MM Provisioning",
                            "featureAccessType": "OFF"
                          }
                        ]
                      }
                    ]
                  }

                  if (profileType === "PREMIUM_AGENT") {
                    payload.accessAllEntryPoints = String(data["Entry Point Access"]);
                    payload.accessAllSites = String(data["Site Access"]);
                    payload.accessAllQueues = String(data["Queue Access"]);
                    payload.accessAllTeams = String(data["Team Access"]);
                    payload.accessAllModules = "SPECIFIC";
                    payload.userProfileAppModules = [
                      {
                        "appModuleId": "m_agent_desktop",
                        "moduleAccessType": "NONE",
                        "userProfileAppFeature": []
                      },
                      {
                        "appModuleId": "m_multimedia",
                        "moduleAccessType": "VIEW",
                        "userProfileAppFeature": [
                          {
                            "appFeatureId": "f_mm_basic_digital",
                            "appFeatureName": "Basic Digital",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_mm_social_channel",
                            "appFeatureName": "Social Channel",
                            "featureAccessType": "ON"
                          },
                          {
                            "appFeatureId": "f_mm_agent_desktop",
                            "appFeatureName": "MM Agent Desktop",
                            "featureAccessType": "OFF"
                          },
                          {
                            "appFeatureId": "f_mm_provisioning",
                            "appFeatureName": "MM Provisioning",
                            "featureAccessType": "OFF"
                          }
                        ]
                      }
                    ]
                  }

                  // Add access control lists based on 'Access' columns
                  payload.entryPointAccess = String(dataRow["Entry Point Access"]).toUpperCase();
                  if (payload.entryPointAccess === "SPECIFIC" && entryPointIds.length > 0) {
                      payload.entryPointIds = entryPointIds;
                  }

                  payload.siteAccess = String(dataRow["Site Access"]).toUpperCase();
                  if (payload.siteAccess === "SPECIFIC" && siteIds.length > 0) {
                      payload.siteIds = siteIds;
                  }

                  payload.queueAccess = String(dataRow["Queue Access"]).toUpperCase();
                  if (payload.queueAccess === "SPECIFIC" && queueIds.length > 0) {
                      payload.queueIds = queueIds;
                  }

                  payload.teamAccess = String(dataRow["Team Access"]).toUpperCase();
                  if (payload.teamAccess === "SPECIFIC" && teamIds.length > 0) {
                      payload.teamIds = teamIds;
                  }
                }
                return payload;
              };

              const fetchUserProfileByName = async (name, org, token, addProgressMessage) => {
                  const allUserProfiles = await getuserProfiles(null, org, token, addProgressMessage);
                  if (Array.isArray(allUserProfiles)) {
                      return name ? allUserProfiles.find(up => up.profileName === name) : allUserProfiles;
                  }
                  return name ? null : [];
              };

              try {
                const userProfileId = await getOrFetchOrCreateAndCache(
                  cacheRegistry,
                  "UserProfileCache",
                  profileName,
                  fetchUserProfileByName,
                  createUserProfile,
                  userProfilePayloadBuilder,
                  orgid,
                  accesstoken,
                  addProgressMessage,
                  excelRowIndex,
                  "User Profile",
                  data
                );

                if (userProfileId) {
                  addProgressMessage(
                    `Successfully processed User Profile "${profileName}". (Excel Row: ${excelRowIndex})`,
                    "success"
                  );
                } else {
                  addProgressMessage(
                    `Failed to process User Profile "${profileName}". (Excel Row: ${excelRowIndex})`,
                    "error"
                  );
                }
              } catch (error) {
                addProgressMessage(
                  `Error processing User Profile "${profileName}": ${
                    error.message || String(error)
                  } (Excel Row: ${excelRowIndex})`,
                  "error"
                );
              }
            }
            break;
          default:
            // No specific processing logic for other sheets yet. Messages already handled above.
            break;
        }
      }
      addProgressMessage("Spreadsheet processing complete!", "success");
    } catch (error) {
      console.error("Fatal script calls:", error);
      addProgressMessage(
        `Fatal Error during script calls: ${error.message || String(error)}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Determine message color based on type
  const getMessageStyle = (type) => {
    switch (type) {
      case "success":
        return { color: "#28a745", fontWeight: "bold" }; // Green
      case "error":
        return { color: "#dc3545", fontWeight: "bold" }; // Red
      case "warning":
        return { color: "#ffc107", fontStyle: "italic" }; // Yellow/Orange
      case "info":
      default:
        return { color: "#007bff" }; // Blue
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "800px",
        margin: "auto",
      }}
    >
      <h1
        style={{
          color: "#333",
          borderBottom: "2px solid #28a745",
          paddingBottom: "10px",
        }}
      >
        Spreadsheet Processor
      </h1>
      <div
        style={{
          backgroundColor: "#e2f0d9",
          border: "1px solid #c3e6cb",
          borderRadius: "5px",
          padding: "10px",
          marginBottom: "20px",
        }}
      >
        <strong>Important:</strong> Before proceeding with the upload, ensure
        the file is saved without macros enabled.
        <p style={{ margin: "5px 0 0 0", fontSize: "0.9em" }}>
          The macros on this document are for <strong>internal</strong> use
          only.
        </p>
        <ul style={{ margin: "10px 0 0 20px", padding: 0, fontSize: "0.9em" }}>
          <li>
            To do this, go to File -> Save As -> Name the file whatever you
            would like -> Ensure the file extension is <strong>.xlsx</strong>{" "}
            and not <strong>.xlsm</strong>. Doing this will automatically
            disable the macros.
          </li>
        </ul>
      </div>

      <div
        style={{
          marginBottom: "20px",
          padding: "15px",
          border: "1px solid #b8daff",
          borderRadius: "5px",
          backgroundColor: "#e0f2ff",
        }}
      >
        <p style={{ margin: "0 0 10px 0" }}>
          <strong>Current Credentials:</strong>
        </p>
        <p style={{ margin: "0" }}>
          Organization ID:{" "}
          <span style={{ fontWeight: "bold" }}>{orgid || "N/A"}</span>
        </p>
        <p style={{ margin: "0" }}>
          Access Token:{" "}
          <span style={{ fontWeight: "bold" }}>
            {accesstoken ? "********" : "N/A"}
          </span>
        </p>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          style={{
            padding: "8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            marginRight: "10px",
          }}
        />
        <button
          onClick={processSheetData}
          disabled={loading || !fileData}
          style={{
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: loading || !fileData ? "not-allowed" : "pointer",
            opacity: loading || !fileData ? 0.6 : 1,
            transition: "background-color 0.3s ease",
          }}
        >
          {loading ? "Processing..." : "Run Script"}
        </button>
      </div>

      {loading && <p style={{ color: "#007bff" }}>Loading... Please wait.</p>}

      <div
        style={{
          borderTop: "1px solid #eee",
          paddingTop: "20px",
          marginTop: "20px",
        }}
      >
        <h2 style={{ color: "#333" }}>Progress Log:</h2>
        <button
          onClick={handleExportLog}
          disabled={progress.length === 0}
          style={{
            padding: "8px 15px",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: progress.length === 0 ? "not-allowed" : "pointer",
            opacity: progress.length === 0 ? 0.6 : 1,
            marginBottom: "15px",
            transition: "background-color 0.3s ease",
          }}
        >
          Export Log
        </button>
        <ul style={{ listStyleType: "none", padding: 0 }}>
          {progress.map((item, index) => (
            <li
              key={index}
              style={{
                marginBottom: "5px",
                fontSize: "0.95em",
                ...getMessageStyle(item.type),
              }}
            >
              <span style={{ fontWeight: "normal", color: "#666" }}>
                [{item.timestamp}]{" "}
              </span>
              {item.message}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SpreadsheetProcessor;